import { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FileText, Download, Printer, Plus, Trash2, Upload, Save, Users, Settings, History, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Position definitions with rates and translations
const POSITIONS = [
  { id: 'scaffold_l1', label: 'Scaffolder Level 1 (Junior)', rate: 230, responsibilities: {
    en: [
      'Works under direct supervision of L2/L3 or Team Leader.',
      'Assists with basic assembly and dismantling tasks.',
      'Follows instructions and learns standard procedures.',
      'Maintains work area cleanliness.'
    ],
    no: [
      'Jobber under direkte veiledning av L2/L3 eller Teamleder.',
      'Bistår med grunnleggende monterings- og demonteringsoppgaver.',
      'Følger instruksjoner og lærer standard prosedyrer.',
      'Holder arbeidsområdet rent.'
    ],
    ro: [
      'Lucrează sub supravegherea directă a L2/L3 sau Liderului de echipă.',
      'Asistă la sarcinile de bază de montare și demontare.',
      'Urmează instrucțiunile și învață procedurile standard.',
      'Menține curățenia zonei de lucru.'
    ]
  }, limitations: {
    en: ['May not work independently on complex structures.', 'May not represent the company to clients.'],
    no: ['Kan ikke arbeide selvstendig på komplekse konstruksjoner.', 'Kan ikke representere selskapet overfor kunder.'],
    ro: ['Nu poate lucra independent pe structuri complexe.', 'Nu poate reprezenta compania în fața clienților.']
  }},
  { id: 'scaffold_l2', label: 'Scaffolder Level 2 (Experienced)', rate: 240, responsibilities: {
    en: [
      'Assembles scaffolding independently according to drawings and instructions.',
      'Understands and follows basic technical sketches and requirements (as applicable).',
      'Guides Level 1 workers (1–2 persons) and ensures correct methods are used.',
      'Performs basic quality checks before calling for inspection.',
      'Maintains expected productivity and reports obstacles early.'
    ],
    no: [
      'Monterer stillas selvstendig i henhold til tegninger og instruksjoner.',
      'Forstår og følger grunnleggende tekniske skisser og krav (der det er relevant).',
      'Veileder Nivå 1-arbeidere (1-2 personer) og sikrer riktige metoder.',
      'Utfører grunnleggende kvalitetskontroller før inspeksjon.',
      'Opprettholder forventet produktivitet og rapporterer hindringer tidlig.'
    ],
    ro: [
      'Montează schele independent conform desenelor și instrucțiunilor.',
      'Înțelege și urmează schițele tehnice de bază și cerințele (unde este cazul).',
      'Ghidează lucrătorii de Nivel 1 (1-2 persoane) și asigură utilizarea metodelor corecte.',
      'Efectuează verificări de calitate de bază înainte de inspecție.',
      'Menține productivitatea așteptată și raportează obstacolele devreme.'
    ]
  }, limitations: {
    en: ['May not represent the company to the client unless delegated by management.'],
    no: ['Kan ikke representere selskapet overfor kunden med mindre delegert av ledelsen.'],
    ro: ['Nu poate reprezenta compania în fața clientului decât dacă este delegat de conducere.']
  }},
  { id: 'scaffold_l3', label: 'Scaffolder Level 3 (Senior)', rate: 260, responsibilities: {
    en: [
      'Plans and executes complex scaffold builds (stairs, roofs, cantilever, heavy-duty).',
      'Reads and interprets technical drawings independently.',
      'Guides L1 and L2 scaffolders, ensures safety and quality standards.',
      'Performs comprehensive quality inspections before requesting final approval from the Team Leader or Foreman.',
      'Proactively identifies and resolves site issues.'
    ],
    no: [
      'Planlegger og utfører komplekse stillasbygg (trapper, tak, utkragning, tunglast).',
      'Leser og tolker tekniske tegninger selvstendig.',
      'Veileder L1 og L2 stillasarbeidere, sikrer sikkerhet og kvalitetsstandarder.',
      'Utfører omfattende kvalitetsinspeksjoner før endelig godkjenning bes fra Teamleder eller Formann.',
      'Identifiserer og løser problemer på byggeplassen proaktivt.'
    ],
    ro: [
      'Planifică și execută construcții complexe de schele (scări, acoperișuri, consolă, sarcini grele).',
      'Citește și interpretează desenele tehnice independent.',
      'Ghidează lucrătorii L1 și L2, asigură standardele de siguranță și calitate.',
      'Efectuează inspecții comprehensive de calitate înainte de a solicita aprobarea finală de la Team Leader sau Foreman.',
      'Identifică și rezolvă proactiv problemele de pe șantier.'
    ]
  }, limitations: {
    en: ['May not sign contracts or represent the company externally without authorization.'],
    no: ['Kan ikke signere kontrakter eller representere selskapet eksternt uten autorisasjon.'],
    ro: ['Nu poate semna contracte sau reprezenta compania extern fără autorizație.']
  }},
  { id: 'team_leader', label: 'Team Leader', rate: 280, responsibilities: {
    en: [
      'Coordinates daily tasks for a team of scaffolders.',
      'Leads SJA/toolbox talks and ensures HSE compliance.',
      'Communicates progress, risks, and needs to management.',
      'Ensures timely completion of projects within scope.',
      'Mentors team members and addresses performance issues.'
    ],
    no: [
      'Koordinerer daglige oppgaver for et team av stillasarbeidere.',
      'Leder SJA/verktøykassesamtaler og sikrer HMS-etterlevelse.',
      'Kommuniserer fremdrift, risikoer og behov til ledelsen.',
      'Sikrer rettidig fullføring av prosjekter innenfor omfang.',
      'Veileder teammedlemmer og tar tak i ytelsesproblemer.'
    ],
    ro: [
      'Coordonează sarcinile zilnice pentru o echipă de constructori de schele.',
      'Conduce discuții SJA/toolbox și asigură conformitatea HSE.',
      'Comunică progresul, riscurile și nevoile conducerii.',
      'Asigură finalizarea la timp a proiectelor în domeniul stabilit.',
      'Mentorează membrii echipei și abordează problemele de performanță.'
    ]
  }, limitations: {
    en: ['May not negotiate rates or contracts without management approval.'],
    no: ['Kan ikke forhandle priser eller kontrakter uten godkjenning fra ledelsen.'],
    ro: ['Nu poate negocia tarife sau contracte fără aprobarea conducerii.']
  }},
  { id: 'foreman', label: 'Foreman', rate: 300, order: 5, responsibilities: {
    en: [
      '**Leadership & Project Coordination**',
      'Oversees multiple scaffolding teams across several projects.',
      'Plans daily operations together with the Project Manager.',
      'Allocates manpower and equipment efficiently between projects.',
      'Supervises Team Leaders and ensures smooth coordination between teams.',
      '**Client Communication**',
      'Acts as the primary on-site contact for clients.',
      'Attends site meetings when required.',
      'Resolves operational issues and communicates project progress.',
      '**Quality & Safety**',
      'Ensures all scaffolding work complies with company standards and applicable regulations.',
      'Verifies scaffold quality before handover.',
      'Ensures HSE procedures are followed by all teams.',
      'Conducts site inspections and supports incident investigations when required.',
      '**Operations**',
      'Monitors productivity, project progress, and deadlines.',
      'Coordinates material requirements with the warehouse and logistics.',
      'Identifies risks and implements corrective actions.',
      'Supports Project Managers in planning and execution.',
      '**People Management**',
      'Coaches Team Leaders and scaffolding crews.',
      'Evaluates employee performance.',
      'Assists with onboarding and training of new employees.',
      'Promotes teamwork, accountability, and a positive work environment.',
      '**Reporting**',
      'Reports directly to the Operations Manager / Project Manager.',
      'Submits daily and weekly progress reports.',
      'Reports delays, incidents, quality issues, and resource needs.',
      'Handles escalations and resolves complex operational issues.'
    ],
    no: [
      '**Ledelse & Prosjektkoordinering**',
      'Overvåker flere stillaslag på tvers av flere prosjekter.',
      'Planlegger daglige operasjoner sammen med Prosjektlederen.',
      'Allokerer mannskap og utstyr effektivt mellom prosjekter.',
      'Overvåker Teamledere og sikrer god koordinering mellom teamene.',
      '**Kundekommunikasjon**',
      'Fungerer som primær kontaktperson på stedet for kunder.',
      'Deltar på byggeplassmøter når det kreves.',
      'Løser operasjonelle problemer og kommuniserer prosjektfremdrift.',
      '**Kvalitet & Sikkerhet**',
      'Sikrer at alt stillasarbeid overholder selskapets standarder og gjeldende regelverk.',
      'Verifiserer stillasets kvalitet før overlevering.',
      'Sikrer at HMS-prosedyrer følges av alle team.',
      'Utfører inspeksjoner på byggeplassen og støtter hendelsesundersøkelser ved behov.',
      '**Drift**',
      'Overvåker produktivitet, prosjektfremdrift og tidsfrister.',
      'Koordinerer materialbehov med lager og logistikk.',
      'Identifiserer risiko og iverksetter korrigerende tiltak.',
      'Støtter Prosjektledere i planlegging og gjennomføring.',
      '**Personalledelse**',
      'Veileder Teamledere og stillasmannskap.',
      'Evaluerer ansattes prestasjoner.',
      'Bistår med onboarding og opplæring av nye ansatte.',
      'Fremmer teamarbeid, ansvarlighet og et positivt arbeidsmiljø.',
      '**Rapportering**',
      'Rapporterer direkte til Driftsleder / Prosjektleder.',
      'Leverer daglige og ukentlige fremdriftsrapporter.',
      'Rapporterer forsinkelser, hendelser, kvalitetsproblemer og ressursbehov.',
      'Håndterer eskalering og løser komplekse operasjonelle problemer.'
    ],
    ro: [
      '**Leadership & Coordonare proiect**',
      'Supraveghează mai multe echipe de schele pe mai multe proiecte.',
      'Planifică operațiunile zilnice împreună cu Managerul de Proiect.',
      'Alocă forța de muncă și echipamentele eficient între proiecte.',
      'Supraveghează Team Leaderii și asigură coordonarea între echipe.',
      '**Comunicare cu clientul**',
      'Este contactul principal pe șantier pentru clienți.',
      'Participă la ședințele de șantier atunci când este necesar.',
      'Rezolvă problemele operaționale și comunică progresul proiectului.',
      '**Calitate & Siguranță**',
      'Asigură conformitatea lucrărilor cu standardele companiei și reglementările aplicabile.',
      'Verifică calitatea schelei înainte de predare.',
      'Asigură respectarea procedurilor HSE de către toate echipele.',
      'Efectuează inspecții pe șantier și sprijină investigarea incidentelor la nevoie.',
      '**Operațiuni**',
      'Monitorizează productivitatea, progresul și termenele proiectelor.',
      'Coordonează necesarul de materiale cu depozitul și logistica.',
      'Identifică riscuri și implementează măsuri corective.',
      'Sprijină Managerii de Proiect în planificare și execuție.',
      '**Managementul oamenilor**',
      'Mentorează Team Leaderii și echipele de schelari.',
      'Evaluează performanța angajaților.',
      'Asistă la onboarding-ul și pregătirea noilor angajați.',
      'Promovează munca în echipă, responsabilitatea și un mediu de lucru pozitiv.',
      '**Raportare**',
      'Raportează direct Managerului de Operațiuni / Managerului de Proiect.',
      'Trimite rapoarte zilnice și săptămânale de progres.',
      'Raportează întârzieri, incidente, probleme de calitate și necesar de resurse.',
      'Gestionează escaladările și rezolvă probleme operaționale complexe.'
    ]
  }, limitations: {
    en: ['Financial decisions require management approval.'],
    no: ['Økonomiske beslutninger krever godkjenning fra ledelsen.'],
    ro: ['Deciziile financiare necesită aprobarea conducerii.']
  }},
  { id: 'warehouse', label: 'Warehouse Responsible', rate: 250, nonScaffoldProgression: true, responsibilities: {
    en: [
      '**Inventory Management**',
      'Manages inventory of scaffolding materials, tools, and equipment.',
      'Ensures accurate counting, tracking, and stock control.',
      'Performs regular inventory checks.',
      '**Logistics**',
      'Coordinates deliveries and returns for all projects.',
      'Prepares materials according to project requirements.',
      'Ensures efficient loading and unloading operations.',
      '**Warehouse Operations**',
      'Maintains proper storage and organization of materials.',
      'Keeps the warehouse clean, safe, and efficient.',
      'Identifies damaged equipment and arranges repairs or replacement.',
      '**Reporting**',
      'Reports shortages, damages, and stock discrepancies promptly.',
      'Coordinates with Project Managers and Foreman regarding material availability.',
    ],
    no: [
      '**Lagerstyring**',
      'Administrerer inventar av stillasmaterialer, verktøy og utstyr.',
      'Sikrer nøyaktig telling, sporing og lagerkontroll.',
      'Utfører regelmessige inventaropptellinger.',
      '**Logistikk**',
      'Koordinerer leveranser og returer for alle prosjekter.',
      'Klargjør materialer i henhold til prosjektkrav.',
      'Sikrer effektiv lasting og lossing.',
      '**Lagerdrift**',
      'Opprettholder riktig lagring og organisering av materialer.',
      'Holder lageret rent, trygt og effektivt.',
      'Identifiserer skadet utstyr og sørger for reparasjon eller erstatning.',
      '**Rapportering**',
      'Rapporterer mangler, skader og lageravvik umiddelbart.',
      'Koordinerer med Prosjektledere og Formann angående materialtilgjengelighet.',
    ],
    ro: [
      '**Managementul inventarului**',
      'Gestionează inventarul de materiale, unelte și echipamente pentru schele.',
      'Asigură numărarea, urmărirea și controlul precis al stocurilor.',
      'Efectuează inventare periodice.',
      '**Logistică**',
      'Coordonează livrările și returnările pentru toate proiectele.',
      'Pregătește materialele conform cerințelor proiectelor.',
      'Asigură operațiuni eficiente de încărcare și descărcare.',
      '**Operațiuni depozit**',
      'Menține depozitarea și organizarea corespunzătoare a materialelor.',
      'Păstrează depozitul curat, sigur și eficient.',
      'Identifică echipamentele deteriorate și organizează repararea sau înlocuirea.',
      '**Raportare**',
      'Raportează prompt lipsurile, daunele și neconcordanțele de stoc.',
      'Coordonează cu Managerii de Proiect și Foreman-ul privind disponibilitatea materialelor.',
    ]
  }, limitations: {
    en: ['Procurement decisions above NOK 5,000 require approval.'],
    no: ['Innkjøpsbeslutninger over NOK 5 000 krever godkjenning.'],
    ro: ['Deciziile de achiziție peste 5.000 NOK necesită aprobare.']
  }},
  { id: 'crane_driver_g8', label: 'Crane Truck Driver (G8) / Logistics Operator', rate: 300, noOvertime: true, nonScaffoldProgression: true, responsibilities: {
    en: [
      '**Transport & Crane Operation:**',
      'Operate crane truck (G8) for loading and unloading scaffolding materials.',
      'Transport scaffolding equipment, tools and materials between warehouse and project sites.',
      'Secure loads properly according to transport regulations.',
      'Ensure safe crane operation during loading and unloading.',
      'Follow all traffic regulations and company transport procedures.',
      'Plan efficient transport routes when assigned.',
      '**Warehouse & Logistics Support:**',
      'When transport tasks are not required, assist with warehouse operations including:',
      '- Preparation and sorting of scaffolding materials for upcoming projects',
      '- Loading and unloading trucks',
      '- Inspection and organization of equipment',
      '- Returning materials to correct storage locations',
      '- Assisting with inventory and stock control',
      '- Maintaining order and cleanliness in the warehouse',
      '**Scaffolding Support:**',
      'When required, may assist scaffolding teams with:',
      '- Transport of materials on site',
      '- Basic scaffolding assembly and dismantling tasks',
      '- Handling scaffolding components and tools',
      '- Supporting scaffolding teams during logistics operations',
      '**Equipment Responsibility:**',
      'Perform daily inspection of the crane truck and lifting equipment.',
      'Report any technical issues immediately.',
      'Maintain the vehicle in clean and safe condition.',
      'Ensure lifting equipment is used according to safety standards.',
      '**Safety & Compliance:**',
      'Follow all HSE procedures and safety rules.',
      'Ensure safe lifting and transport operations.',
      'Report incidents, hazards or unsafe situations immediately.'
    ],
    no: [
      '**Transport og kranoperasjon:**',
      'Betjene kranbil (G8) for lasting og lossing av stillasmaterialer.',
      'Transportere stillasutstyr, verktøy og materialer mellom lager og prosjektsteder.',
      'Sikre last riktig i henhold til transportforskrifter.',
      'Sikre trygg kranoperasjon under lasting og lossing.',
      'Følge alle trafikkforskrifter og selskapets transportprosedyrer.',
      'Planlegge effektive transportruter når tildelt.',
      '**Lager- og logistikkstøtte:**',
      'Når transportoppgaver ikke er nødvendig, bistå med lageroperasjoner inkludert:',
      '- Forberedelse og sortering av stillasmaterialer for kommende prosjekter',
      '- Lasting og lossing av lastebiler',
      '- Inspeksjon og organisering av utstyr',
      '- Returnering av materialer til riktige lagerplasser',
      '- Assistere med inventar og lagerkontroll',
      '- Opprettholde orden og renslighet i lageret',
      '**Stillasstøtte:**',
      'Når nødvendig, kan bistå stillasteam med:',
      '- Transport av materialer på stedet',
      '- Grunnleggende stillasmontering og demontering',
      '- Håndtering av stillaskomponenter og verktøy',
      '- Støtte stillasteam under logistikkoperasjoner',
      '**Utstyrsansvar:**',
      'Utføre daglig inspeksjon av kranbilen og løfteutstyret.',
      'Rapportere tekniske problemer umiddelbart.',
      'Holde kjøretøyet i ren og sikker stand.',
      'Sikre at løfteutstyr brukes i henhold til sikkerhetsstandarder.',
      '**Sikkerhet og etterlevelse:**',
      'Følge alle HMS-prosedyrer og sikkerhetsregler.',
      'Sikre trygge løfte- og transportoperasjoner.',
      'Rapportere hendelser, farer eller usikre situasjoner umiddelbart.'
    ],
    ro: [
      '**Transport și operare macara:**',
      'Operează camionul cu macara (G8) pentru încărcare și descărcare de materiale pentru schele.',
      'Transportă echipamente de schele, unelte și materiale între depozit și șantiere.',
      'Asigură încărcăturile corespunzător conform reglementărilor de transport.',
      'Asigură operarea în siguranță a macaralei în timpul încărcării și descărcării.',
      'Respectă toate regulamentele de trafic și procedurile de transport ale companiei.',
      'Planifică rute de transport eficiente când este alocat.',
      '**Suport depozit și logistică:**',
      'Când sarcinile de transport nu sunt necesare, asistă la operațiunile de depozit inclusiv:',
      '- Pregătirea și sortarea materialelor de schele pentru proiecte viitoare',
      '- Încărcare și descărcare camioane',
      '- Inspecția și organizarea echipamentelor',
      '- Returnarea materialelor în locațiile corecte de depozitare',
      '- Asistență la inventar și controlul stocurilor',
      '- Menținerea ordinii și curățeniei în depozit',
      '**Suport schele:**',
      'Când este necesar, poate asista echipele de schele cu:',
      '- Transport de materiale pe șantier',
      '- Sarcini de bază de montare și demontare schele',
      '- Manipularea componentelor și uneltelor de schele',
      '- Sprijinirea echipelor de schele în operațiuni logistice',
      '**Responsabilitate echipament:**',
      'Efectuează inspecția zilnică a camionului cu macara și a echipamentului de ridicare.',
      'Raportează imediat orice probleme tehnice.',
      'Menține vehiculul în stare curată și sigură.',
      'Asigură că echipamentul de ridicare este utilizat conform standardelor de siguranță.',
      '**Siguranță și conformitate:**',
      'Respectă toate procedurile HSE și regulile de siguranță.',
      'Asigură operațiuni sigure de ridicare și transport.',
      'Raportează imediat incidentele, pericolele sau situațiile nesigure.'
    ]
  }, limitations: {
    en: [
      'Work Flexibility: Due to the operational nature of the company, the employee may be assigned different tasks related to transport, warehouse operations or scaffolding work depending on project and logistics needs.',
      'Important: The position includes both transport and operational support tasks. When transport assignments are not required, the employee is expected to assist with warehouse and scaffolding operations.',
      'Salary reflects a flexible logistics and operational role including transport, warehouse and scaffolding support tasks.'
    ],
    no: [
      'Arbeidsfleksibilitet: På grunn av selskapets operasjonelle natur kan arbeidstakeren bli tildelt forskjellige oppgaver relatert til transport, lageroperasjoner eller stillasarbeid avhengig av prosjekt- og logistikkbehov.',
      'Viktig: Stillingen inkluderer både transport og operasjonelle støtteoppgaver. Når transportoppdrag ikke er nødvendig, forventes arbeidstakeren å bistå med lager- og stillasoperasjoner.',
      'Lønn reflekterer en fleksibel logistikk- og operasjonell rolle inkludert transport, lager og stillasstøtteoppgaver.'
    ],
    ro: [
      'Flexibilitate la locul de muncă: Datorită naturii operaționale a companiei, angajatul poate fi repartizat la diferite sarcini legate de transport, operațiuni de depozit sau lucrări de schele în funcție de nevoile proiectului și logisticii.',
      'Important: Poziția include atât sarcini de transport, cât și de suport operațional. Când sarcinile de transport nu sunt necesare, angajatul trebuie să asiste la operațiunile de depozit și schele.',
      'Salariul reflectă un rol flexibil de logistică și operațional, incluzând transport, depozit și sarcini de suport pentru schele.'
    ]
  }}
];

// Storage keys
const STORAGE_KEYS = {
  employees: 'doserne_employees',
  contracts: 'doserne_contracts',
  settings: 'doserne_contract_settings'
};

// Contract translations
const CONTRACT_TRANSLATIONS = {
  title: {
    en: 'STANDARD EMPLOYMENT CONTRACT',
    no: 'STANDARD ARBEIDSKONTRAKT',
    ro: 'CONTRACT STANDARD DE MUNCĂ'
  },
  sections: {
    parties: { en: 'Parties', no: 'Parter', ro: 'Părțile' },
    employer: { en: 'Employer', no: 'Arbeidsgiver', ro: 'Angajator' },
    employee: { en: 'Employee', no: 'Arbeidstaker', ro: 'Angajat' },
    employmentDetails: { en: 'Employment details', no: 'Ansettelsesdetaljer', ro: 'Detalii angajare' },
    startDate: { en: 'Start date', no: 'Startdato', ro: 'Data începerii' },
    contractDate: { en: 'Contract date', no: 'Kontraktdato', ro: 'Data contractului' },
    contractType: { en: 'Contract type', no: 'Kontrakttype', ro: 'Tip contract' },
    placeOfWork: { en: 'Place of work', no: 'Arbeidssted', ro: 'Locul de muncă' },
    workingHours: { en: 'Working hours', no: 'Arbeidstid', ro: 'Program de lucru' },
    normalWorkingTime: { en: 'Normal working time', no: 'Normal arbeidstid', ro: 'Timp normal de lucru' },
    hoursPerWeek: { en: '37.5 hours per week (effective working time)', no: '37.5 timer per uke (effektiv arbeidstid)', ro: '37.5 ore pe săptămână (timp efectiv de lucru)' },
    dailyBreak: { en: 'Daily break', no: 'Daglig pause', ro: 'Pauza zilnică' },
    breakMinutes: { en: '30 minutes unpaid break', no: '30 minutter ubetalt pause', ro: '30 minute pauză neplătită' },
    ordinaryHours: { en: 'Ordinary hours', no: 'Ordinære timer', ro: 'Ore ordinare' },
    overtimeThreshold: { en: 'Overtime threshold', no: 'Overtidsgrense', ro: 'Prag ore suplimentare' },
    overtimeAfter40: { en: 'Overtime is work exceeding the statutory working hours of 40 hours per week', no: 'Overtid er arbeid som overstiger den lovbestemte arbeidstiden på 40 timer per uke', ro: 'Orele suplimentare sunt munca ce depășește durata legală de lucru de 40 de ore pe săptămână' },
    transportTitle: { en: 'Transport from outside Oslo:', no: 'Transport fra utenfor Oslo:', ro: 'Transport din afara Oslo:' },
    transportText: { en: 'Transport from outside the city to Oslo is paid as normal working hour. Activity must be recorded in Tripletex:', no: 'Transport fra utenfor byen til Oslo betales som normal arbeidstime. Aktiviteten må registreres i Tripletex:', ro: 'Transportul din afara orașului către Oslo este plătit ca oră normală de lucru. Activitatea trebuie înregistrată în Tripletex:' },
    overtimeNote: { en: 'Overtime may be required within legal limits. Overtime must be pre-approved by management unless immediate HSE reasons require action.', no: 'Overtid kan kreves innenfor lovlige grenser. Overtid må forhåndsgodkjennes av ledelsen med mindre umiddelbare HMS-grunner krever handling.', ro: 'Orele suplimentare pot fi necesare în limitele legale. Orele suplimentare trebuie aprobate în prealabil de conducere, cu excepția cazurilor HSE urgente.' },
    salary: { en: 'Salary & payments', no: 'Lønn og utbetalinger', ro: 'Salariu și plăți' },
    hourlyRate: { en: 'Hourly rate', no: 'Timesats', ro: 'Tarif orar' },
    paymentDate: { en: 'Payment date', no: 'Utbetalingsdato', ro: 'Data plății' },
    overtime: { en: 'Overtime', no: 'Overtid', ro: 'Ore suplimentare' },
    weekday: { en: 'weekday', no: 'hverdag', ro: 'zi lucrătoare' },
    sunHolidays: { en: 'Sun/holidays & 21:00–06:00', no: 'Søn/helligdager & 21:00–06:00', ro: 'Dum/sărbători & 21:00–06:00' },
    otpPension: { en: 'OTP pension', no: 'OTP pensjon', ro: 'Pensie OTP' },
    bankAccount: { en: 'Bank Account', no: 'Bankkonto', ro: 'Cont bancar' },
    equipment: { en: 'Equipment & Tools', no: 'Utstyr og verktøy', ro: 'Echipament și unelte' },
    equipmentIntro: { en: 'The company provides necessary work equipment and personal protective equipment (PPE).', no: 'Selskapet tilbyr nødvendig arbeidsutstyr og personlig verneutstyr (PPE).', ro: 'Compania furnizează echipamentul de lucru necesar și echipamentul de protecție personală (EIP).' },
    responsibility: { en: 'Responsibility', no: 'Ansvar', ro: 'Responsabilitate' },
    responsibilityText: { en: 'Each employee is responsible for the equipment they receive', no: 'Hver ansatt er ansvarlig for utstyret de mottar', ro: 'Fiecare angajat este responsabil de echipamentul pe care îl primește' },
    return: { en: 'Return', no: 'Retur', ro: 'Returnare' },
    returnText: { en: 'All equipment must be returned upon termination of employment', no: 'Alt utstyr må returneres ved opphør av arbeidsforhold', ro: 'Tot echipamentul trebuie returnat la încetarea contractului de muncă' },
    replacement: { en: 'Replacement', no: 'Erstatning', ro: 'Înlocuire' },
    replacementText: { en: 'Damaged equipment will be replaced - report any damage immediately', no: 'Skadet utstyr vil bli erstattet - rapporter skader umiddelbart', ro: 'Echipamentul deteriorat va fi înlocuit - raportați orice deteriorare imediat' },
    lossDamage: { en: 'Loss/Damage', no: 'Tap/Skade', ro: 'Pierdere/Deteriorare' },
    lossDamageText: { 
      en: 'In cases of intentional misuse or gross negligence resulting in loss or damage of company equipment, the company may seek compensation in accordance with applicable Norwegian labour law.', 
      no: 'I tilfeller av forsettlig misbruk eller grov uaktsomhet som resulterer i tap eller skade på selskapets utstyr, kan selskapet kreve erstatning i samsvar med gjeldende norsk arbeidsrett.', 
      ro: 'În cazurile de utilizare abuzivă intenționată sau neglijență gravă care duce la pierderea sau deteriorarea echipamentului companiei, compania poate solicita despăgubiri în conformitate cu legislația norvegiană a muncii aplicabilă.' 
    },
    inspection: { en: 'Inspection', no: 'Inspeksjon', ro: 'Inspecție' },
    inspectionText: { 
      en: 'Employees must inspect their tools and safety equipment daily before use and report any defects immediately.', 
      no: 'Ansatte må inspisere verktøyet og sikkerhetsutstyret sitt daglig før bruk og rapportere eventuelle feil umiddelbart.', 
      ro: 'Angajații trebuie să inspecteze zilnic uneltele și echipamentul de siguranță înainte de utilizare și să raporteze imediat orice defecte.' 
    },
    holiday: { en: 'Holiday', no: 'Ferie', ro: 'Concediu' },
    holidayIntro: { en: 'Holiday and holiday pay are regulated by', no: 'Ferie og feriepenger er regulert av', ro: 'Concediul și plata concediului sunt reglementate de' },
    holidayEntitlement: { en: 'Holiday entitlement', no: 'Ferierett', ro: 'Drept la concediu' },
    weeksAndDay: { en: '4 weeks + 1 day', no: '4 uker + 1 dag', ro: '4 săptămâni + 1 zi' },
    holidayPay: { en: 'Holiday pay', no: 'Feriepenger', ro: 'Plata concediului' },
    holidayPayPercent: { en: '10.2% (or higher if applicable)', no: '10,2% (eller høyere hvis aktuelt)', ro: '10,2% (sau mai mult dacă este cazul)' },
    holidayNotice: { en: 'Important notice requirements:', no: 'Viktige varselkrav:', ro: 'Cerințe importante de notificare:' },
    holiday2Months: { en: 'Holiday requests must be submitted at least 2 months in advance', no: 'Ferieforespørsler må sendes inn minst 2 måneder i forveien', ro: 'Cererile de concediu trebuie depuse cu cel puțin 2 luni în avans' },
    holidayRefuse: { 
      en: 'Management may adjust holiday periods based on operational needs in accordance with the Norwegian Holiday Act (Ferieloven).', 
      no: 'Ledelsen kan justere ferieperioder basert på driftsmessige behov i samsvar med ferieloven.', 
      ro: 'Conducerea poate ajusta perioadele de concediu în funcție de nevoile operaționale, în conformitate cu Legea norvegiană a concediilor (Ferieloven).' 
    },
    holidayApproval: { en: 'Approval is subject to operational needs and project schedules', no: 'Godkjenning avhenger av driftsbehov og prosjektplaner', ro: 'Aprobarea depinde de nevoile operaționale și programul proiectelor' },
    trialPeriod: { en: 'Trial period', no: 'Prøvetid', ro: 'Perioadă de probă' },
    trialPeriodText: { en: '6 months (if applicable)', no: '6 måneder (hvis aktuelt)', ro: '6 luni (dacă este cazul)' },
    noticeDuringTrial: { en: 'Notice during trial', no: 'Oppsigelse under prøvetid', ro: 'Preaviz în perioada de probă' },
    noticeDuringTrialText: { en: '14 days (mutual), unless otherwise required by law', no: '14 dager (gjensidig), med mindre annet kreves ved lov', ro: '14 zile (reciproc), cu excepția cazurilor prevăzute de lege' },
    termination: { en: 'Termination', no: 'Oppsigelse', ro: 'Încetare contract' },
    first14Days: { en: 'Special conditions during first 14 days:', no: 'Spesielle vilkår de første 14 dagene:', ro: 'Condiții speciale în primele 14 zile:' },
    first14DaysText: { en: 'During the first 14 working days, both parties may terminate the employment with 1 day notice if something is not satisfactory.', no: 'I løpet av de første 14 arbeidsdagene kan begge parter si opp arbeidsforholdet med 1 dags varsel hvis noe ikke er tilfredsstillende.', ro: 'În primele 14 zile lucrătoare, ambele părți pot înceta contractul cu preaviz de 1 zi dacă ceva nu este satisfăcător.' },
    after14Days: { en: 'After 14 days (during trial period):', no: 'Etter 14 dager (under prøvetid):', ro: 'După 14 zile (în perioada de probă):' },
    after14DaysText: { en: 'Notice period is 14 days (mutual) until the 6-month trial period ends.', no: 'Oppsigelsestiden er 14 dager (gjensidig) til 6-måneders prøvetid utløper.', ro: 'Perioada de preaviz este de 14 zile (reciproc) până la sfârșitul perioadei de probă de 6 luni.' },
    after6Months: { en: 'After trial period (6 months):', no: 'Etter prøvetid (6 måneder):', ro: 'După perioada de probă (6 luni):' },
    after6MonthsRenewal: { en: 'The contract will be renewed with a new employment period.', no: 'Kontrakten vil bli fornyet med en ny ansettelsesperiode.', ro: 'Contractul va fi reînnoit cu o nouă perioadă de muncă.' },
    standardNotice: { en: 'Standard notice: 3 months mutual notice, calculated from the 1st of the month following the notice date.', no: 'Standard oppsigelse: 3 måneders gjensidig oppsigelse, regnet fra den 1. i måneden etter oppsigelsesdatoen.', ro: 'Preaviz standard: 3 luni preaviz reciproc, calculat de la data de 1 a lunii următoare datei notificării.' },
    additionalNotes: { en: 'Additional Notes', no: 'Tilleggsmerknader', ro: 'Note suplimentare' },
    signatureDate: { en: 'Date', no: 'Dato', ro: 'Data' },
    signaturePlace: { en: 'Place', no: 'Sted', ro: 'Locul' },
    employerSignature: { en: 'Employer Signature', no: 'Arbeidsgivers signatur', ro: 'Semnătura angajatorului' },
    employeeSignature: { en: 'Employee Signature', no: 'Arbeidstakers signatur', ro: 'Semnătura angajatului' },
    // Working hours flexibility
    workingHoursFlexibility: { 
      en: 'Working hours may vary depending on project requirements.', 
      no: 'Arbeidstiden kan variere avhengig av prosjektkrav.', 
      ro: 'Programul de lucru poate varia în funcție de cerințele proiectului.' 
    },
    // Operational Instructions
    operationalInstructions: { en: 'Operational Instructions', no: 'Operasjonelle instruksjoner', ro: 'Instrucțiuni operaționale' },
    complianceTitle: { en: 'Compliance with Instructions', no: 'Etterlevelse av instruksjoner', ro: 'Conformitatea cu instrucțiunile' },
    complianceText: { 
      en: 'The employee must follow all lawful and reasonable instructions given by the employer, Project Leader, Foreman or Team Leader regarding work tasks, work methods, safety procedures and organization of the work. Refusal to follow such instructions without valid reason may be considered a breach of employment obligations and may result in disciplinary action.', 
      no: 'Arbeidstakeren må følge alle lovlige og rimelige instruksjoner gitt av arbeidsgiver, prosjektleder, formann eller teamleder angående arbeidsoppgaver, arbeidsmetoder, sikkerhetsprosedyrer og organisering av arbeidet. Å nekte å følge slike instruksjoner uten gyldig grunn kan anses som brudd på arbeidsforpliktelser og kan føre til disiplinærtiltak.', 
      ro: 'Angajatul trebuie să urmeze toate instrucțiunile legale și rezonabile date de angajator, Liderul de Proiect, Șeful de Șantier sau Liderul de Echipă cu privire la sarcinile de lucru, metodele de lucru, procedurile de siguranță și organizarea muncii. Refuzul de a urma astfel de instrucțiuni fără un motiv valid poate fi considerat o încălcare a obligațiilor de muncă și poate duce la măsuri disciplinare.' 
    },
    flexibilityTitle: { en: 'Work Tasks Flexibility', no: 'Fleksibilitet i arbeidsoppgaver', ro: 'Flexibilitatea sarcinilor de lucru' },
    flexibilityText: { 
      en: 'Due to the nature of construction and scaffolding work, the employee may be assigned different work tasks, work locations or responsibilities depending on project needs and operational requirements.', 
      no: 'På grunn av bygg- og stillasarbeidets natur kan arbeidstakeren bli tildelt forskjellige arbeidsoppgaver, arbeidssteder eller ansvarsområder avhengig av prosjektbehov og operasjonelle krav.', 
      ro: 'Din cauza naturii lucrărilor de construcții și schele, angajatul poate fi repartizat la diferite sarcini de lucru, locații de muncă sau responsabilități în funcție de nevoile proiectului și cerințele operaționale.' 
    },
    workPaceTitle: { en: 'Work Pace', no: 'Arbeidstempo', ro: 'Ritmul de lucru' },
    workPaceText: { 
      en: 'Employees are expected to perform their duties with reasonable efficiency and contribute actively to the productivity of the team.', 
      no: 'Ansatte forventes å utføre sine oppgaver med rimelig effektivitet og bidra aktivt til teamets produktivitet.', 
      ro: 'Se așteaptă ca angajații să își îndeplinească sarcinile cu o eficiență rezonabilă și să contribuie activ la productivitatea echipei.' 
    }
  }
};

function HoldingContractGenerator({ doserneMode = false }) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('generate');

  // Doserne assignment (when embedded under /doserne): pick a colleague to
  // send the generated contract to (push + signing link + email).
  const [dosEmail, setDosEmail] = useState("");
  const [dosSending, setDosSending] = useState(false);
  useEffect(() => {
    if (!doserneMode) return;
    (async () => {
      try {
        const token = localStorage.getItem("scaffoldx_token");
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/doserne/admin/members`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const members = res.data?.members || [];
        // Feed the "Select existing employee" dropdown from Doserne members.
        setEmployees(members.map((m) => ({
          id: m.email,
          name: m.full_name || m.email,
          email: m.email,
          personalId: m.personal_id || "",
          address: m.address || "",
          phone: m.phone || "",
          position: m.position || "",
          bankAccount: m.bank_account || "",
        })));
      } catch { /* ignore */ }
    })();
  }, [doserneMode]);
  
  // Employee form state
  const [employees, setEmployees] = useState([]);
  const [newEmployee, setNewEmployee] = useState({
    name: '', personalId: '', address: '', phone: '', email: '', position: '', bankAccount: ''
  });
  
  // Contract form state
  const [contractData, setContractData] = useState({
    employeeId: '',
    contractType: 'Permanent',
    empName: '',
    empId: '',
    empAddress: '',
    empPhone: '',
    empEmail: '',
    empPosition: '',
    empBankAccount: '',
    startDate: '',
    endDate: '',  // Only used for Temporary / Project-based
    contractDate: new Date().toISOString().split('T')[0],
    position: 'scaffold_l2',
    hourlyRate: 240,
    otWeekday: '50%',
    otSpecial: '100%',
    workHours: '07:00–15:00',
    placeOfWork: 'Oslo and surrounding areas (East & South). Work elsewhere in Norway may be required.',
    payDate: '5th of each month (weekend: nearest business day)',
    notes: '',
    language: 'en',  // Contract language: en, no, ro
    customTasks: []  // Custom tasks/responsibilities added manually
  });
  
  // State for new custom task input
  const [newCustomTask, setNewCustomTask] = useState('');
  
  // Contract history
  const [contracts, setContracts] = useState([]);
  
  // Settings
  const [settings, setSettings] = useState({
    companyName: 'Doserne Utleie AS',
    companyOrg: '937 207 050',
    companyAddress: 'Brobekkveien 100, 0582 Oslo',
    warehouse: 'Brobekkveien 100, 0582 Oslo; Hadelandsparken 8, 2742 Grua',
    otp: '2%',
    rateLock: 'locked',
    adminPin: ''
  });
  
  // Signature refs
  const employerSigRef = useRef(null);
  const employeeSigRef = useRef(null);
  const [employerSig, setEmployerSig] = useState(null);
  const [employeeSig, setEmployeeSig] = useState(null);
  
  // Handle signature image upload
  const handleSignatureUpload = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const imageData = event.target.result;
        if (type === 'employer') {
          setEmployerSig(imageData);
          localStorage.setItem('employer_signature', imageData);
        } else {
          setEmployeeSig(imageData);
          localStorage.setItem('employee_signature', imageData);
        }
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Clear signature
  const clearSignature = (type) => {
    if (type === 'employer') {
      setEmployerSig(null);
      localStorage.removeItem('employer_signature');
    } else {
      setEmployeeSig(null);
      localStorage.removeItem('employee_signature');
    }
  };
  
  // Load data from localStorage
  useEffect(() => {
    const savedEmployees = localStorage.getItem(STORAGE_KEYS.employees);
    const savedContracts = localStorage.getItem(STORAGE_KEYS.contracts);
    const savedSettings = localStorage.getItem(STORAGE_KEYS.settings);
    const savedEmployerSig = localStorage.getItem('employer_signature');
    const savedEmployeeSig = localStorage.getItem('employee_signature');
    
    if (savedEmployees) setEmployees(JSON.parse(savedEmployees));
    if (savedContracts) setContracts(JSON.parse(savedContracts));
    if (savedSettings) setSettings(prev => ({ ...prev, ...JSON.parse(savedSettings) }));
    if (savedEmployerSig) setEmployerSig(savedEmployerSig);
    if (savedEmployeeSig) setEmployeeSig(savedEmployeeSig);
  }, []);
  
  // Live positions loaded from API (falls back to hardcoded if API unavailable)
  const [livePositions, setLivePositions] = useState(POSITIONS);

  // Cumulative responsibilities toggle (show responsibilities from higher levels as "Growth path")
  const [showCumulative, setShowCumulative] = useState(false);
  // Per-item checkbox selection for lower-level inherited responsibilities (only shown when TL)
  // Key = `${positionId}:${index}`, value = false to hide
  const [selectedLowerResp, setSelectedLowerResp] = useState({});

  useEffect(() => {
    // Fetch latest positions from backend on mount
    (async () => {
      try {
        const token = localStorage.getItem("scaffoldx_token");
        if (!token) return;
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/contract-settings/positions`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (Array.isArray(res.data) && res.data.length) {
          // Merge: keep hardcoded items that DB doesn't have (e.g., crane_driver_g8)
          const dbIds = new Set(res.data.map(p => p.id));
          const merged = [
            ...res.data,
            ...POSITIONS.filter(p => !dbIds.has(p.id)),
          ];
          setLivePositions(merged);
        }
      } catch (err) {
        console.warn("Could not load contract positions from API, using defaults");
      }
    })();
  }, []);

  // Save data to localStorage
  const saveToStorage = (key, data) => {
    localStorage.setItem(key, JSON.stringify(data));
  };
  
  // Get position details (uses live positions from API + fallback to hardcoded)
  const getPosition = (posId) => livePositions.find(p => p.id === posId) || livePositions[1] || POSITIONS[1];
  
  // Helper function to get translated text
  const t = (key) => CONTRACT_TRANSLATIONS.sections[key]?.[contractData.language] || CONTRACT_TRANSLATIONS.sections[key]?.en || key;
  
  // Handle position change
  const handlePositionChange = (posId) => {
    const pos = getPosition(posId);
    setContractData(prev => ({
      ...prev,
      position: posId,
      hourlyRate: settings.rateLock === 'locked' ? pos.rate : prev.hourlyRate
    }));
  };
  
  // Handle employee selection
  const handleEmployeeSelect = (empId) => {
    if (!empId) {
      setContractData(prev => ({
        ...prev,
        employeeId: '',
        empName: '',
        empId: '',
        empAddress: '',
        empPhone: '',
        empEmail: '',
        empPosition: '',
        empBankAccount: ''
      }));
      return;
    }
    
    const emp = employees.find(e => e.id === empId);
    if (emp) {
      setContractData(prev => ({
        ...prev,
        employeeId: empId,
        empName: emp.name,
        empId: emp.personalId,
        empAddress: emp.address,
        empPhone: emp.phone,
        empEmail: emp.email || '',
        empPosition: emp.position || '',
        empBankAccount: emp.bankAccount || ''
      }));
      if (doserneMode) setDosEmail(emp.email || empId);
    }
  };
  
  // Add new employee
  const handleAddEmployee = () => {
    if (!newEmployee.name || !newEmployee.personalId) {
      toast.error('Name and Personal ID are required');
      return;
    }
    
    const emp = {
      id: `emp_${Date.now()}`,
      ...newEmployee,
      createdAt: new Date().toISOString()
    };
    
    const updated = [...employees, emp];
    setEmployees(updated);
    saveToStorage(STORAGE_KEYS.employees, updated);
    setNewEmployee({ name: '', personalId: '', address: '', phone: '', email: '', position: '', bankAccount: '' });
    toast.success('Employee added successfully');
  };
  
  // Delete employee
  const handleDeleteEmployee = (empId) => {
    const updated = employees.filter(e => e.id !== empId);
    setEmployees(updated);
    saveToStorage(STORAGE_KEYS.employees, updated);
    toast.success('Employee removed');
  };
  
  // Save contract to history
  const handleSaveContract = () => {
    if (!contractData.empName) {
      toast.error('Please fill in employee details');
      return;
    }
    
    const contract = {
      id: `contract_${Date.now()}`,
      ...contractData,
      positionLabel: getPosition(contractData.position).label,
      savedAt: new Date().toISOString()
    };
    
    const updated = [contract, ...contracts];
    setContracts(updated);
    saveToStorage(STORAGE_KEYS.contracts, updated);
    toast.success('Contract saved to history');
  };
  
  // Load contract from history
  const handleLoadContract = (contract) => {
    setContractData({
      employeeId: contract.employeeId || '',
      contractType: contract.contractType,
      empName: contract.empName,
      empId: contract.empId,
      empAddress: contract.empAddress,
      empPhone: contract.empPhone,
      empEmail: contract.empEmail,
      startDate: contract.startDate,
      endDate: contract.endDate || '',
      contractDate: contract.contractDate,
      position: contract.position,
      hourlyRate: contract.hourlyRate,
      otWeekday: contract.otWeekday,
      otSpecial: contract.otSpecial,
      workHours: contract.workHours,
      placeOfWork: contract.placeOfWork,
      payDate: contract.payDate,
      notes: contract.notes || ''
    });
    setActiveTab('generate');
    toast.success('Contract loaded');
  };
  
  // Delete contract from history
  const handleDeleteContract = (contractId) => {
    const updated = contracts.filter(c => c.id !== contractId);
    setContracts(updated);
    saveToStorage(STORAGE_KEYS.contracts, updated);
    toast.success('Contract removed from history');
  };
  
  // Save settings
  const handleSaveSettings = () => {
    saveToStorage(STORAGE_KEYS.settings, settings);
    toast.success('Settings saved');
  };
  
  // Export functions
  const handleExportEmployees = () => {
    const dataStr = JSON.stringify(employees, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `doserne_employees_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };
  
  const handleExportContracts = () => {
    const dataStr = JSON.stringify(contracts, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `doserne_contracts_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };
  
  // Import functions
  const handleImportEmployees = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result);
        if (Array.isArray(data)) {
          setEmployees(data);
          saveToStorage(STORAGE_KEYS.employees, data);
          toast.success(`Imported ${data.length} employees`);
        }
      } catch {
        toast.error('Invalid JSON file');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };
  
  const handleImportContracts = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result);
        if (Array.isArray(data)) {
          setContracts(data);
          saveToStorage(STORAGE_KEYS.contracts, data);
          toast.success(`Imported ${data.length} contracts`);
        }
      } catch {
        toast.error('Invalid JSON file');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };
  
  // Download contract as Word document (.doc) - editable in MS Word, LibreOffice, Google Docs
  const buildContractHtml = () => {
    const empName = (contractData.empName || 'contract').replace(/[^a-zA-Z0-9_-]/g, '_');
    const filename = `Kontrakt_${empName}_${contractData.contractDate}.doc`;

    // Inline critical styles so Word renders them
    const styles = `
      <style>
        body { font-family: Calibri, Arial, sans-serif; font-size: 11pt; line-height: 1.4; color: #000; }
        h1, h2, h3 { font-family: Calibri, Arial, sans-serif; }
        table { border-collapse: collapse; width: 100%; margin-bottom: 12pt; }
        td { padding: 4pt 8pt; vertical-align: top; border: 1px solid #d1d5db; }
        .section-header { background: #23F7DD; color: #000; padding: 6pt 10pt; font-weight: bold; font-size: 12pt; margin-top: 16pt; }
        .section-body { padding: 10pt; border: 1px solid #d1d5db; border-top: 0; margin-bottom: 14pt; }
        .label { color: #6b7280; font-weight: normal; }
        .career-path { background: #eff6ff; border-left: 4px solid #60a5fa; padding: 8pt 10pt; margin-top: 8pt; }
        .career-path .pos-title { font-weight: bold; margin-top: 6pt; }
        .warning-box { background: #fffbeb; border-left: 4px solid #f59e0b; padding: 6pt 10pt; margin: 6pt 0; font-style: italic; }
        ul { margin: 4pt 0 4pt 18pt; padding: 0; }
        li { margin-bottom: 2pt; }
        .signature-block { margin-top: 30pt; }
        .signature-line { border-top: 1px solid #000; width: 250pt; margin-top: 40pt; padding-top: 4pt; }
      </style>
    `;

    // Build a cleaner Word-friendly HTML from contractData (more reliable than scraping the live preview)
    const lang = contractData.language;
    const pos = currentPosition;
    const isOT = !pos.noOvertime;

    const tL = (key) => CONTRACT_TRANSLATIONS.sections[key]?.[lang] || CONTRACT_TRANSLATIONS.sections[key]?.en || key;

    const titleText =
      lang === 'no' ? 'ARBEIDSAVTALE' :
      lang === 'ro' ? 'CONTRACT DE MUNCĂ' :
      'EMPLOYMENT CONTRACT';

    const sec01Title = lang === 'no' ? 'Partene' : lang === 'ro' ? 'Părțile' : 'Parties';
    const sec03Title = lang === 'en' ? 'Position & responsibilities' : lang === 'no' ? 'Stilling og ansvar' : 'Poziție și responsabilități';
    const validUntilLabel = lang === 'en' ? 'Valid until' : lang === 'no' ? 'Gyldig til' : 'Valabil până la';
    const validUntilWarn =
      lang === 'en' ? 'If the employee is not notified at least 14 days before the expiry date about a new contract being prepared, this contract is considered NOT renewed and employment ends automatically on the date above.' :
      lang === 'no' ? 'Dersom den ansatte ikke er varslet minst 14 dager før utløpsdatoen om at en ny kontrakt vil bli utstedt, anses denne kontrakten som IKKE fornyet, og arbeidsforholdet opphører automatisk på datoen over.' :
      'Dacă angajatul nu este informat cu cel puțin 14 zile înainte de data de expirare despre încheierea unui contract nou, acest contract este considerat NEREÎNNOIT, iar raportul de muncă încetează automat la data de mai sus.';

    const formatDate = (iso) => {
      if (!iso) return '';
      const d = new Date(iso);
      const locale = lang === 'no' ? 'no-NO' : lang === 'ro' ? 'ro-RO' : 'en-GB';
      return d.toLocaleDateString(locale, { day: '2-digit', month: 'long', year: 'numeric' });
    };

    // Helper: render responsibilities as Word HTML with **bold headers** support
    const renderRespItems = (items) => {
      return items.map(r => {
        if (/^\*\*.+\*\*$/.test(r)) {
          const clean = r.replace(/^\*\*|\*\*$/g, '');
          return `</ul><p style="font-weight:bold; margin-top:6pt; margin-bottom:2pt;">${clean}</p><ul>`;
        }
        return `<li>${r}</li>`;
      }).join('');
    };

    const respList = `<ul>${renderRespItems(pos.responsibilities?.[lang] || pos.responsibilities?.en || [])}</ul>`;
    const limList = (pos.limitations?.[lang] || pos.limitations?.en || [])
      .map(l => `<li>${l}</li>`).join('');
    const customTasks = contractData.customTasks.map(t => `<li><i>${t}</i></li>`).join('');

    // Career path block (cumulative) — show BOTH future path AND inherited base
    let careerPathHtml = '';
    if (showCumulative) {
      const currentOrder = pos.order || 0;
      const isProgressionJob = (p) => !p.nonScaffoldProgression && (p.order || 0) > 0;
      const higher = livePositions
        .filter(p => isProgressionJob(p) && (p.order || 0) > currentOrder && p.id !== pos.id)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      const lower = livePositions
        .filter(p => isProgressionJob(p) && (p.order || 0) < currentOrder && p.id !== pos.id && p.responsibilities)
        .sort((a, b) => (a.order || 0) - (b.order || 0));

      const parts = [];

      if (higher.length) {
        const cpTitle = lang === 'en' ? 'Career growth path — future responsibilities'
          : lang === 'no' ? 'Karrierevei — fremtidige ansvar'
          : 'Cale de creștere — responsabilități viitoare';
        const blocks = higher.map(hp => {
          const list = hp.responsibilities?.[lang] || hp.responsibilities?.en || [];
          return `
            <div class="pos-title">${hp.label}</div>
            <ul>${renderRespItems(list)}</ul>
          `;
        }).join('');
        parts.push(`<div class="career-path" style="background: #eff6ff; border-left: 4px solid #60a5fa;">
          <p><b>${cpTitle}</b></p>
          ${blocks}
        </div>`);
      }

      if (lower.length) {
        const cpTitle = lang === 'en' ? 'Includes selected responsibilities from lower levels'
          : lang === 'no' ? 'Inkluderer valgte ansvar fra lavere nivåer'
          : 'Include responsabilitățile selectate de la nivelurile inferioare';
        const blocks = lower.map(hp => {
          const list = hp.responsibilities?.[lang] || hp.responsibilities?.en || [];
          const filtered = list.filter((_, i) => selectedLowerResp[`${hp.id}:${i}`] !== false);
          if (!filtered.length) return '';
          return `
            <div class="pos-title">${hp.label}</div>
            <ul>${renderRespItems(filtered)}</ul>
          `;
        }).filter(Boolean).join('');
        if (blocks) {
          parts.push(`<div class="career-path" style="background: #ecfdf5; border-left: 4px solid #10b981;">
            <p><b>${cpTitle}</b></p>
            ${blocks}
          </div>`);
        }
      }

      careerPathHtml = parts.join('');
    }

    const body = `
      <h1 style="text-align:center; margin-bottom: 24pt;">${titleText}</h1>

      <div class="section-header">01 — ${sec01Title}</div>
      <div class="section-body">
        <table>
          <tr><td style="width:50%">
            <b>${settings.companyName}</b> (Org.nr: ${settings.companyOrg})<br>
            ${settings.companyAddress}<br><br>
            <b>${lang === 'no' ? 'Lager' : lang === 'ro' ? 'Depozit' : 'Warehouse'}:</b> ${settings.warehouse}
          </td><td style="width:50%">
            <span class="label">${lang === 'no' ? 'Navn' : lang === 'ro' ? 'Nume' : 'Name'}:</span> ${contractData.empName || '{{Full Name}}'}<br>
            <span class="label">${lang === 'no' ? 'Person ID' : lang === 'ro' ? 'CNP' : 'Personal ID'}:</span> ${contractData.empId || '{{ID}}'}<br>
            <span class="label">${lang === 'no' ? 'Adresse' : lang === 'ro' ? 'Adresa' : 'Address'}:</span> ${contractData.empAddress || '{{Address}}'}<br>
            <span class="label">${lang === 'no' ? 'Telefon' : lang === 'ro' ? 'Telefon' : 'Phone'}:</span> ${contractData.empPhone || '{{Phone}}'}<br>
            <span class="label">E-post:</span> ${contractData.empEmail || '{{Email}}'}
          </td></tr>
        </table>
      </div>

      <div class="section-header">02 — ${tL('employmentDetails')}</div>
      <div class="section-body">
        <table>
          <tr><td class="label" style="width:30%">${tL('startDate')}</td><td>${contractData.startDate || '{{Start Date}}'}</td></tr>
          <tr><td class="label">${tL('contractDate')}</td><td>${contractData.contractDate}</td></tr>
          <tr><td class="label">${tL('contractType')}</td><td>${contractData.contractType}</td></tr>
          ${contractData.contractType !== 'Permanent' && contractData.endDate ? `
            <tr><td class="label"><b>${validUntilLabel}</b></td><td><b>${formatDate(contractData.endDate)}</b></td></tr>
          ` : ''}
          <tr><td class="label">${tL('placeOfWork')}</td><td>${contractData.placeOfWork}</td></tr>
        </table>
        ${contractData.contractType !== 'Permanent' && contractData.endDate ? `
          <div class="warning-box">⚠️ ${validUntilWarn}</div>
        ` : ''}
      </div>

      <div class="section-header">03 — ${sec03Title}</div>
      <div class="section-body">
        <p><b>${lang === 'no' ? 'Stilling' : lang === 'ro' ? 'Poziție' : 'Position'}:</b> ${pos.label}</p>
        <p class="label" style="margin-top:8pt;">${lang === 'en' ? 'Role expectations' : lang === 'no' ? 'Rolleforventninger' : 'Așteptări de rol'}</p>
        ${respList}
        ${customTasks ? `<ul>${customTasks}</ul>` : ''}
        ${careerPathHtml}
        <p class="label" style="margin-top:8pt;">${lang === 'en' ? 'Limitations / authority' : lang === 'no' ? 'Begrensninger / myndighet' : 'Limitări / autoritate'}</p>
        <ul>${limList}</ul>
      </div>

      <div class="section-header">04 — ${tL('workingHours')}</div>
      <div class="section-body">
        <table>
          <tr><td class="label" style="width:30%">${tL('normalWorkingTime')}</td><td>${tL('hoursPerWeek')}</td></tr>
          <tr><td class="label">${tL('dailyBreak')}</td><td>${tL('breakMinutes')}</td></tr>
          <tr><td class="label">${tL('ordinaryHours')}</td><td>${contractData.workHours}</td></tr>
          ${isOT ? `<tr><td class="label">${tL('overtimeThreshold')}</td><td>${tL('overtimeAfter40')}</td></tr>` : ''}
        </table>
        <p style="font-style:italic; margin-top: 6pt;">${tL('workingHoursFlexibility')}</p>
      </div>

      <div class="section-header">05 — ${tL('salary')}</div>
      <div class="section-body">
        <table>
          <tr><td class="label" style="width:30%">${tL('hourlyRate')}</td><td>${contractData.hourlyRate} NOK/hour</td></tr>
          <tr><td class="label">${tL('paymentDate')}</td><td>${contractData.payDate}</td></tr>
          ${isOT ? `<tr><td class="label">${tL('overtime')}</td><td>${contractData.otWeekday} (${tL('weekday')}) • ${contractData.otSpecial} (${tL('sunHolidays')})</td></tr>` : ''}
          <tr><td class="label">${tL('otpPension')}</td><td>${settings.otp}</td></tr>
          ${contractData.empBankAccount ? `<tr><td class="label">${tL('bankAccount')}</td><td>${contractData.empBankAccount}</td></tr>` : ''}
        </table>
      </div>

      <div class="section-header">06 — ${tL('equipment')}</div>
      <div class="section-body">
        <p>${tL('equipmentIntro')}</p>
        <ul>
          <li><b>${tL('responsibility')}:</b> ${tL('responsibilityText')}</li>
          <li><b>${tL('return')}:</b> ${tL('returnText')}</li>
          <li><b>${tL('replacement')}:</b> ${tL('replacementText')}</li>
          <li><b>${tL('inspection')}:</b> ${tL('inspectionText')}</li>
          <li><b>${tL('lossDamage')}:</b> ${tL('lossDamageText')}</li>
        </ul>
      </div>

      <div class="section-header">07 — ${tL('holiday')}</div>
      <div class="section-body">
        <p>${tL('holidayIntro')} <b>Ferieloven</b>.</p>
        <p><b>${tL('holidayEntitlement')}:</b> ${tL('weeksAndDay')}. <b>${tL('holidayPay')}:</b> ${tL('holidayPayPercent')}.</p>
        <div class="warning-box">
          <b>${tL('holidayNotice')}</b>
          <ul style="margin-top:4pt;">
            <li>${tL('holiday2Months')}</li>
            <li>${tL('holidayRefuse')}</li>
            <li>${tL('holidayApproval')}</li>
          </ul>
        </div>
      </div>

      <div class="section-header">08 — ${tL('trialPeriod')}</div>
      <div class="section-body">
        <p><b>${tL('trialPeriod')}:</b> ${tL('trialPeriodText')}</p>
        <p><b>${tL('noticeDuringTrial')}:</b> ${tL('noticeDuringTrialText')}</p>
      </div>

      <div class="section-header">09 — ${tL('termination')}</div>
      <div class="section-body">
        <p><b>${tL('first14Days')}</b> — ${tL('first14DaysText')}</p>
        <p style="margin-top:6pt;">${tL('terminationText') || ''}</p>
      </div>

      <div class="signature-block">
        <table>
          <tr>
            <td style="width:50%; border:0;">
              <div class="signature-line"></div>
              <p><b>${lang === 'no' ? 'Arbeidsgiver' : lang === 'ro' ? 'Angajator' : 'Employer'}</b></p>
              <p>${settings.companyName}</p>
            </td>
            <td style="width:50%; border:0;">
              <div class="signature-line"></div>
              <p><b>${lang === 'no' ? 'Arbeidstaker' : lang === 'ro' ? 'Angajat' : 'Employee'}</b></p>
              <p>${contractData.empName || ''}</p>
            </td>
          </tr>
        </table>
      </div>

      ${contractData.notes ? `<p style="margin-top:20pt;"><b>${lang === 'no' ? 'Merknader' : lang === 'ro' ? 'Mențiuni' : 'Notes'}:</b> ${contractData.notes}</p>` : ''}
    `;

    // Wrap in Word-compatible XML
    const wordHtml = `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head>
  <meta charset="utf-8">
  <title>${titleText}</title>
  <!--[if gte mso 9]>
  <xml>
    <w:WordDocument>
      <w:View>Print</w:View>
      <w:Zoom>100</w:Zoom>
    </w:WordDocument>
  </xml>
  <![endif]-->
  ${styles}
</head>
<body>
  ${body}
</body>
</html>`;

    const blob = new Blob(['\ufeff', wordHtml], { type: 'application/msword' });
    return { wordHtml, filename, blob };
  };

  const handleDownloadWord = () => {
    const preview = document.querySelector('[data-testid="contract-preview"]');
    if (!preview) {
      toast.error('Contract preview not found');
      return;
    }
    const { blob, filename } = buildContractHtml();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success('Word document downloaded');
  };

  // Generate the contract PDF on the backend and send it to a Doserne colleague
  // for signing (push notification + signing link + email).
  const handleSendToColleague = async () => {
    if (!dosEmail) { toast.error('Alege un coleg / Velg en kollega'); return; }
    if (!contractData.empName) { toast.error('Completează numele angajatului'); return; }
    setDosSending(true);
    try {
      const { wordHtml, filename } = buildContractHtml();
      const token = localStorage.getItem('scaffoldx_token');
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/doserne/admin/contracts/generate-html`,
        { email: dosEmail, html: wordHtml, filename: filename.replace(/\.doc$/, '.pdf') },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Contract trimis colegului spre semnare 📄 (notificare + email)');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Eroare la trimitere');
    }
    setDosSending(false);
  };

  // Print contract
  const handlePrint = () => {
    // Create print styles
    const printStyles = document.createElement('style');
    printStyles.id = 'contract-print-styles';
    printStyles.innerHTML = `
      @media print {
        @page {
          size: A4;
          margin: 15mm;
        }
        
        body * {
          visibility: hidden;
        }
        
        [data-testid="contract-preview"],
        [data-testid="contract-preview"] * {
          visibility: visible;
        }
        
        [data-testid="contract-preview"] {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          background: white !important;
          color: black !important;
          font-size: 11pt;
          line-height: 1.4;
        }
        
        /* Hide non-contract elements */
        nav, header, footer, aside, button, .no-print {
          display: none !important;
        }
        
        /* Contract styling */
        .border {
          border: 1px solid #ddd !important;
        }
        
        .rounded-lg {
          border-radius: 8px !important;
        }
        
        .bg-gray-100 {
          background-color: #f3f4f6 !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        
        .bg-gray-50 {
          background-color: #f9fafb !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        
        .bg-blue-50 {
          background-color: #eff6ff !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        
        .bg-amber-50 {
          background-color: #fffbeb !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        
        .bg-red-50 {
          background-color: #fef2f2 !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        
        .bg-green-50 {
          background-color: #f0fdf4 !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        
        .bg-\\[\\#23F7DD\\] {
          background-color: #23F7DD !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        
        /* Text colors */
        .text-blue-800, .text-blue-700 {
          color: #1e40af !important;
        }
        
        .text-amber-800, .text-amber-700 {
          color: #92400e !important;
        }
        
        .text-red-800, .text-red-700 {
          color: #991b1b !important;
        }
        
        .text-green-800, .text-green-700 {
          color: #166534 !important;
        }
        
        .text-gray-500, .text-gray-600 {
          color: #6b7280 !important;
        }
        
        /* Signatures at bottom */
        .border-t {
          border-top: 1px solid #e5e7eb !important;
          page-break-inside: avoid !important;
        }
        
        .border-dashed {
          border-style: dashed !important;
        }
        
        /* Hide upload buttons in print */
        .no-print {
          display: none !important;
        }
        
        /* Show signature images */
        .border-dashed img {
          max-height: 100px !important;
          display: block !important;
        }
        
        /* Purple/Indigo/Teal backgrounds for Section 10 */
        .bg-purple-50 {
          background-color: #faf5ff !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .bg-indigo-50 {
          background-color: #eef2ff !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .bg-teal-50 {
          background-color: #f0fdfa !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .border-purple-500 { border-color: #a855f7 !important; }
        .border-indigo-500 { border-color: #6366f1 !important; }
        .border-teal-500 { border-color: #14b8a6 !important; }
        .text-purple-800, .text-purple-700 { color: #6b21a8 !important; }
        .text-indigo-800, .text-indigo-700 { color: #3730a3 !important; }
        .text-teal-800, .text-teal-700 { color: #115e59 !important; }
        
        /* Page breaks - prevent sections from being cut */
        .space-y-6 > div {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }
        
        /* Each contract section should not be split */
        [data-testid="contract-preview"] .border.rounded-lg {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
          margin-bottom: 8pt;
        }

        /* Exception: Section 03 (Position & responsibilities) when cumulative is on - too tall to keep on one page */
        [data-testid="contract-preview"] .section-03-allow-break {
          page-break-inside: auto !important;
          break-inside: auto !important;
        }

        /* But inside section 03, each individual position card must stay together */
        [data-testid="contract-preview"] .section-03-allow-break .position-card {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }
        
        /* Section headers should keep content together */
        .bg-gray-100.px-4.py-2 {
          page-break-after: avoid !important;
          break-after: avoid !important;
        }
        
        /* Lists should not be split */
        ul, ol {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }
        
        /* Info boxes should stay together */
        .bg-blue-50, .bg-amber-50, .bg-green-50, .bg-red-50, .bg-purple-50, .bg-indigo-50, .bg-teal-50 {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }
        
        /* Make content more compact for print */
        [data-testid="contract-preview"] {
          font-size: 9pt !important;
          line-height: 1.25 !important;
        }
        
        [data-testid="contract-preview"] .p-4 {
          padding: 6pt 8pt !important;
        }
        
        [data-testid="contract-preview"] .space-y-2 > * + * {
          margin-top: 3pt !important;
        }
        
        [data-testid="contract-preview"] .space-y-3 > * + * {
          margin-top: 5pt !important;
        }

        /* Compress Career Growth Path block aggressively to fit on same page as section 03 */
        [data-testid="contract-preview"] .section-03-allow-break .bg-blue-50 {
          padding: 4pt 6pt !important;
          margin-top: 4pt !important;
          font-size: 8pt !important;
          line-height: 1.2 !important;
        }
        [data-testid="contract-preview"] .section-03-allow-break .bg-blue-50 ul li {
          margin-bottom: 1pt !important;
        }
        [data-testid="contract-preview"] .section-03-allow-break .bg-blue-50 .position-card {
          margin-bottom: 4pt !important;
        }
        [data-testid="contract-preview"] .section-03-allow-break .bg-blue-50 p {
          margin-bottom: 2pt !important;
        }

        /* Compress Section 06 (Equipment) — common spillover point */
        [data-testid="contract-preview"] .print-section-06 .p-4 {
          padding: 5pt 7pt !important;
        }
        [data-testid="contract-preview"] .print-section-06 li {
          margin-bottom: 1pt !important;
          font-size: 8.5pt !important;
        }

        /* Compress Section 07 (Holiday) too */
        [data-testid="contract-preview"] .print-section-07 li {
          margin-bottom: 1pt !important;
          font-size: 8.5pt !important;
        }
        
        /* Smaller section number badges */
        [data-testid="contract-preview"] .w-8.h-8 {
          width: 22px !important;
          height: 22px !important;
          font-size: 10pt !important;
        }
        
        /* Orphan/widow control */
        p {
          orphans: 3;
          widows: 3;
        }
        
        /* Signature section - always on new page if needed */
        .border-t.pt-6.mt-6 {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }
        
        /* Force problematic sections to start on new page if near bottom */
        /* (Now controlled by explicit page layout rules below) */
        
        /* ============================================
         * EXPLICIT PAGE LAYOUT (user-defined):
         * Page 1: Section 01 + 02
         * Page 2: Section 03 + 04
         * Page 3: Section 05 + 06 + 07
         * Page 4: Section 08 + 09 + 10 + signature
         * ============================================ */
        [data-testid="contract-preview"] .print-section-03,
        [data-testid="contract-preview"] .print-section-05,
        [data-testid="contract-preview"] .print-section-08 {
          page-break-before: always !important;
          break-before: page !important;
        }

        /* Section 01 must NOT have page-break-before */
        [data-testid="contract-preview"] .print-section-01 {
          page-break-before: avoid !important;
          break-before: avoid !important;
        }

        /* Section 10 stays with 08 + 09 on page 4 */
        .print-section-10 {
          page-break-before: auto !important;
          break-before: auto !important;
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }

        /* Sections 06, 07, 09 avoid splitting internally */
        .print-section-06,
        .print-section-07,
        .print-section-09 {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }

        /* Eliminate trailing blank page */
        [data-testid="contract-preview"] > *:last-child,
        [data-testid="contract-preview"] > div > *:last-child {
          page-break-after: auto !important;
          break-after: auto !important;
          margin-bottom: 0 !important;
        }

        /* Ensure no empty container creates phantom pages */
        [data-testid="contract-preview"] .space-y-6 > div:empty {
          display: none !important;
        }
      }
    `;
    
    // Remove old styles if exist
    const oldStyles = document.getElementById('contract-print-styles');
    if (oldStyles) oldStyles.remove();
    
    // Add new styles
    document.head.appendChild(printStyles);
    
    // Print
    window.print();
    
    // Clean up after print
    setTimeout(() => {
      const styles = document.getElementById('contract-print-styles');
      if (styles) styles.remove();
    }, 1000);
  };
  
  // Get current position
  const currentPosition = getPosition(contractData.position);

  const _content = (
      <div className="space-y-6" data-testid="contract-generator-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            {doserneMode && (
              <a href="/doserne/admin/kontrakter" className="text-emerald-400 text-sm font-semibold hover:underline" data-testid="gen-back-link">← Kontrakter</a>
            )}
            <h1 className="text-2xl font-bold text-white">Contract Generator PRO</h1>
            <p className="text-white/60">Generate employment contracts for scaffolders</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white/5 border border-white/10">
            <TabsTrigger value="generate" className="data-[state=active]:bg-[#23F7DD]/20 data-[state=active]:text-[#23F7DD]">
              <FileText className="w-4 h-4 mr-2" />
              Generate
            </TabsTrigger>
            {!doserneMode && (
            <TabsTrigger value="employees" className="data-[state=active]:bg-[#23F7DD]/20 data-[state=active]:text-[#23F7DD]">
              <Users className="w-4 h-4 mr-2" />
              Employees
            </TabsTrigger>
            )}
            {!doserneMode && (
            <TabsTrigger value="history" className="data-[state=active]:bg-[#23F7DD]/20 data-[state=active]:text-[#23F7DD]">
              <History className="w-4 h-4 mr-2" />
              Contracts
            </TabsTrigger>
            )}
            <TabsTrigger value="settings" className="data-[state=active]:bg-[#23F7DD]/20 data-[state=active]:text-[#23F7DD]">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Generate Tab */}
          <TabsContent value="generate" className="space-y-6">
            {/* Legal Notice */}
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
              <strong className="text-amber-400">Legal-safe template:</strong>{' '}
              <span className="text-white/70">no unpaid overtime, no "holiday must be worked in". Adjust to your tariffs/agreements if needed.</span>
            </div>

            {doserneMode && (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4" data-testid="doserne-assign-panel">
                <div className="flex items-center gap-2 mb-2">
                  <Send className="w-4 h-4 text-emerald-400" />
                  <strong className="text-emerald-300">Trimite colegului spre semnare</strong>
                </div>
                <p className="text-white/60 text-sm mb-3">Alege colegul din „Select existing employee" (lista de mai jos vine din Doserne), completează contractul, apoi trimite. Primește notificare + email cu link de semnat cu degetul; PDF-ul semnat ajunge în profilul lui.</p>
                <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                  <div className="flex-1 text-sm text-white/80" data-testid="doserne-target">
                    {dosEmail ? (<>Coleg selectat: <b className="text-white">{contractData.empName || dosEmail}</b> <span className="text-white/50">({dosEmail})</span></>) : (<span className="text-white/50">Niciun coleg selectat încă.</span>)}
                  </div>
                  <Button onClick={handleSendToColleague} disabled={dosSending || !dosEmail}
                    className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold" data-testid="doserne-send-btn">
                    {dosSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    Generează &amp; trimite
                  </Button>
                </div>
              </div>
            )}

            {/* Form */}
            <Card className="bg-white/5 border-white/10 text-white placeholder:text-white/40">
              <CardHeader>
                <CardTitle className="text-white">Employee Details</CardTitle>
                <CardDescription className="text-white/60">Pick an existing employee or fill manually.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Employee Select */}
                  <div>
                    <Label className="text-white/90 font-medium">Select existing employee</Label>
                    <Select value={contractData.employeeId || "manual"} onValueChange={(v) => handleEmployeeSelect(v === "manual" ? "" : v)}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white placeholder:text-white/40">
                        <SelectValue placeholder="(Manual entry)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manual">(Manual entry)</SelectItem>
                        {employees.map(emp => (
                          <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-white/40 mt-1">{doserneMode ? "Lista vine din colegii Doserne. Alege colegul căruia îi trimiți contractul." : "Tip: manage employees in the Employees tab."}</p>
                  </div>
                  
                  {/* Contract Type */}
                  <div>
                    <Label className="text-white/90 font-medium">Contract type</Label>
                    <Select value={contractData.contractType} onValueChange={(v) => setContractData(prev => ({ ...prev, contractType: v, endDate: v === 'Permanent' ? '' : prev.endDate }))}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white placeholder:text-white/40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Permanent">Permanent</SelectItem>
                        <SelectItem value="Temporary">Temporary</SelectItem>
                        <SelectItem value="Project-based">Project-based</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* End Date (only for Temporary / Project-based) */}
                  {contractData.contractType !== 'Permanent' && (
                    <div data-testid="end-date-section">
                      <Label className="text-white/90 font-medium flex items-center gap-2">
                        Valid until / Gyldig til
                        <span className="text-[#F7931A] text-xs">*</span>
                      </Label>
                      <Input
                        type="date"
                        value={contractData.endDate}
                        onChange={(e) => setContractData(prev => ({ ...prev, endDate: e.target.value }))}
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                        min={contractData.startDate || undefined}
                        data-testid="end-date-input"
                      />
                      <p className="text-xs text-white/50 mt-1">
                        Du får varsel 2 uker før utløp. Hvis ikke fornyet — kontrakten anses som avsluttet.
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Contract Language Selector */}
                <div className="bg-[#23F7DD]/10 border border-[#23F7DD]/30 rounded-lg p-4">
                  <Label className="text-white font-medium mb-2 block">Contract Language / Limba contractului</Label>
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      type="button"
                      variant={contractData.language === 'en' ? 'default' : 'outline'}
                      className={contractData.language === 'en' ? 'bg-[#23F7DD] text-black hover:bg-[#23F7DD]/80' : 'border-white/20 text-white hover:bg-white/10'}
                      onClick={() => setContractData(prev => ({ ...prev, language: 'en' }))}
                    >
                      🇬🇧 English
                    </Button>
                    <Button
                      type="button"
                      variant={contractData.language === 'no' ? 'default' : 'outline'}
                      className={contractData.language === 'no' ? 'bg-[#23F7DD] text-black hover:bg-[#23F7DD]/80' : 'border-white/20 text-white hover:bg-white/10'}
                      onClick={() => setContractData(prev => ({ ...prev, language: 'no' }))}
                    >
                      🇳🇴 Norsk
                    </Button>
                    <Button
                      type="button"
                      variant={contractData.language === 'ro' ? 'default' : 'outline'}
                      className={contractData.language === 'ro' ? 'bg-[#23F7DD] text-black hover:bg-[#23F7DD]/80' : 'border-white/20 text-white hover:bg-white/10'}
                      onClick={() => setContractData(prev => ({ ...prev, language: 'ro' }))}
                    >
                      🇷🇴 Română
                    </Button>
                  </div>
                  <p className="text-xs text-white/50 mt-2">
                    {contractData.language === 'en' && 'The contract and responsibilities will be generated in English.'}
                    {contractData.language === 'no' && 'Kontrakten og ansvarsområdene vil bli generert på norsk.'}
                    {contractData.language === 'ro' && 'Contractul și responsabilitățile vor fi generate în limba română.'}
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Employee Details */}
                  <div>
                    <Label className="text-white/90 font-medium">Full name</Label>
                    <Input 
                      value={contractData.empName}
                      onChange={(e) => setContractData(prev => ({ ...prev, empName: e.target.value }))}
                      placeholder="Full name"
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                      data-testid="emp-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-white/90 font-medium">Personal ID / D-number</Label>
                    <Input 
                      value={contractData.empId}
                      onChange={(e) => setContractData(prev => ({ ...prev, empId: e.target.value }))}
                      placeholder="Personal ID number"
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                    />
                  </div>
                  <div>
                    <Label className="text-white/90 font-medium">Address</Label>
                    <Input 
                      value={contractData.empAddress}
                      onChange={(e) => setContractData(prev => ({ ...prev, empAddress: e.target.value }))}
                      placeholder="Street, city"
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                    />
                  </div>
                  <div>
                    <Label className="text-white/90 font-medium">Phone</Label>
                    <Input 
                      value={contractData.empPhone}
                      onChange={(e) => setContractData(prev => ({ ...prev, empPhone: e.target.value }))}
                      placeholder="+47 ..."
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                    />
                  </div>
                  <div>
                    <Label className="text-white/90 font-medium">Email</Label>
                    <Input 
                      value={contractData.empEmail}
                      onChange={(e) => setContractData(prev => ({ ...prev, empEmail: e.target.value }))}
                      placeholder="name@email.com"
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                    />
                  </div>
                  <div>
                    <Label className="text-white/90 font-medium">Bank Account (IBAN)</Label>
                    <Input 
                      value={contractData.empBankAccount}
                      onChange={(e) => setContractData(prev => ({ ...prev, empBankAccount: e.target.value }))}
                      placeholder="NO12 3456 7890 1234"
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/40 font-mono"
                    />
                  </div>
                </div>

                <div className="border-t border-white/10 pt-4">
                  <h4 className="text-white font-medium mb-4">Employment</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-white/90 font-medium">Start date</Label>
                      <Input 
                        type="date"
                        value={contractData.startDate}
                        onChange={(e) => setContractData(prev => ({ ...prev, startDate: e.target.value }))}
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                      />
                    </div>
                    <div>
                      <Label className="text-white/90 font-medium">Contract date</Label>
                      <Input 
                        type="date"
                        value={contractData.contractDate}
                        onChange={(e) => setContractData(prev => ({ ...prev, contractDate: e.target.value }))}
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                      />
                    </div>
                    <div>
                      <Label className="text-white/90 font-medium">Position</Label>
                      <Select value={contractData.position} onValueChange={handlePositionChange}>
                        <SelectTrigger className="bg-white/5 border-white/10 text-white placeholder:text-white/40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {livePositions.map(pos => (
                            <SelectItem key={pos.id} value={pos.id}>
                              {pos.label} — {pos.rate} NOK
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-white/90 font-medium">Hourly rate (NOK)</Label>
                      <Input 
                        type="number"
                        value={contractData.hourlyRate}
                        onChange={(e) => setContractData(prev => ({ ...prev, hourlyRate: parseInt(e.target.value) }))}
                        disabled={settings.rateLock === 'locked'}
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                      />
                      <p className="text-xs text-white/40 mt-1">
                        {settings.rateLock === 'locked' ? 'Locked by position (recommended).' : 'Editable mode.'}
                      </p>
                    </div>
                    {!currentPosition.noOvertime && (
                      <>
                        <div>
                          <Label className="text-white/90 font-medium">Overtime add-on (weekday)</Label>
                          <Input 
                            value={contractData.otWeekday}
                            onChange={(e) => setContractData(prev => ({ ...prev, otWeekday: e.target.value }))}
                            className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                          />
                        </div>
                        <div>
                          <Label className="text-white/90 font-medium">Overtime (Sun/holidays & 21:00–06:00)</Label>
                          <Input 
                            value={contractData.otSpecial}
                            onChange={(e) => setContractData(prev => ({ ...prev, otSpecial: e.target.value }))}
                            className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                          />
                        </div>
                      </>
                    )}
                    {currentPosition.noOvertime && (
                      <div className="md:col-span-2 p-3 bg-orange-500/20 border border-orange-500/30 rounded-lg">
                        <p className="text-orange-300 text-sm font-medium">
                          ⚠️ No Overtime - This position has a fixed hourly rate that includes all work tasks.
                        </p>
                      </div>
                    )}
                    <div>
                      <Label className="text-white/90 font-medium">Ordinary work hours</Label>
                      <Input 
                        value={contractData.workHours}
                        onChange={(e) => setContractData(prev => ({ ...prev, workHours: e.target.value }))}
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                      />
                    </div>
                    <div>
                      <Label className="text-white/90 font-medium">Place of work</Label>
                      <Input 
                        value={contractData.placeOfWork}
                        onChange={(e) => setContractData(prev => ({ ...prev, placeOfWork: e.target.value }))}
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                      />
                    </div>
                    <div>
                      <Label className="text-white/90 font-medium">Payment date</Label>
                      <Input 
                        value={contractData.payDate}
                        onChange={(e) => setContractData(prev => ({ ...prev, payDate: e.target.value }))}
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                      />
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <Label className="text-white/90 font-medium">Additional notes / addendum (optional)</Label>
                    <Textarea 
                      value={contractData.notes}
                      onChange={(e) => setContractData(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Car allowance, specific client project, special conditions..."
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                    />
                  </div>
                  
                  {/* Custom Tasks Section */}
                  <div className="mt-4 p-4 bg-white/5 rounded-lg border border-white/10">
                    {/* Cumulative toggle */}
                    <div className="flex items-start gap-3 p-3 bg-[#23F7DD]/5 border border-[#23F7DD]/30 rounded-lg mb-4">
                      <input
                        type="checkbox"
                        id="show-cumulative-toggle"
                        checked={showCumulative}
                        onChange={(e) => setShowCumulative(e.target.checked)}
                        className="mt-1 w-4 h-4 accent-[#23F7DD]"
                        data-testid="cumulative-toggle"
                      />
                      <label htmlFor="show-cumulative-toggle" className="flex-1 cursor-pointer">
                        <div className="text-white font-medium text-sm">Show career path / inherited responsibilities</div>
                        <div className="text-white/60 text-xs mt-1">
                          For lower levels (L1, L2) → shows higher levels as "future path". For Team Leader → shows all lower levels as "inherited base" (TL must be able to do everything below).
                        </div>
                      </label>
                    </div>

                    {/* Per-responsibility checkbox picker (only when lower positions exist AND toggle ON) */}
                    {showCumulative && (() => {
                      const currentOrder = currentPosition.order || 0;
                      const isProgressionJob = (p) => !p.nonScaffoldProgression && (p.order || 0) > 0;
                      const lowerPositions = livePositions
                        .filter(p => isProgressionJob(p) && (p.order || 0) < currentOrder && p.id !== currentPosition.id && p.responsibilities)
                        .sort((a, b) => (a.order || 0) - (b.order || 0));

                      if (lowerPositions.length === 0) return null;

                      return (
                        <div className="mb-4 p-3 bg-white/5 border border-emerald-500/30 rounded-lg">
                          <p className="text-emerald-300 text-sm font-medium mb-2">
                            🎯 Bifează responsabilitățile de la nivelurile inferioare care apar în contract:
                          </p>
                          <p className="text-white/50 text-xs mb-3">
                            Debifează cele care nu sunt relevante pentru acest TL (ex: sarcini foarte de bază)
                          </p>
                          <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                            {lowerPositions.map(lp => {
                              const list = lp.responsibilities?.[contractData.language] || lp.responsibilities?.en || [];
                              return (
                                <div key={lp.id} className="border-l-2 border-emerald-500/40 pl-3">
                                  <div className="flex items-center justify-between mb-1">
                                    <p className="text-white font-semibold text-xs">{lp.label}</p>
                                    <div className="flex gap-1">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const next = { ...selectedLowerResp };
                                          list.forEach((_, i) => { next[`${lp.id}:${i}`] = true; });
                                          setSelectedLowerResp(next);
                                        }}
                                        className="text-[10px] text-emerald-300 hover:text-emerald-200 underline"
                                      >
                                        Toate
                                      </button>
                                      <span className="text-white/20 text-[10px]">|</span>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const next = { ...selectedLowerResp };
                                          list.forEach((_, i) => { next[`${lp.id}:${i}`] = false; });
                                          setSelectedLowerResp(next);
                                        }}
                                        className="text-[10px] text-red-300 hover:text-red-200 underline"
                                      >
                                        Niciuna
                                      </button>
                                    </div>
                                  </div>
                                  {list.map((r, i) => {
                                    const key = `${lp.id}:${i}`;
                                    const isHeader = /^\*\*.+\*\*$/.test(r);
                                    if (isHeader) {
                                      return (
                                        <p key={key} className="text-emerald-200 font-semibold text-xs mt-2 mb-1 uppercase tracking-wide">
                                          {r.replace(/^\*\*|\*\*$/g, '')}
                                        </p>
                                      );
                                    }
                                    const checked = selectedLowerResp[key] !== false;
                                    return (
                                      <label key={key} className="flex items-start gap-2 py-1 cursor-pointer hover:bg-white/5 rounded px-1">
                                        <input
                                          type="checkbox"
                                          checked={checked}
                                          onChange={(e) => setSelectedLowerResp({ ...selectedLowerResp, [key]: e.target.checked })}
                                          className="mt-1 w-3.5 h-3.5 accent-emerald-500 flex-shrink-0"
                                          data-testid={`lower-resp-${key}`}
                                        />
                                        <span className={`text-xs leading-relaxed ${checked ? 'text-white/90' : 'text-white/40 line-through'}`}>{r}</span>
                                      </label>
                                    );
                                  })}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}

                    <Label className="text-white/90 font-medium mb-2 block">Custom responsibilities / tasks</Label>
                    <p className="text-white/50 text-xs mb-3">Add custom tasks that will appear in Section 03 (Position & Responsibilities)</p>
                    
                    <div className="flex gap-2 mb-3">
                      <Input 
                        value={newCustomTask}
                        onChange={(e) => setNewCustomTask(e.target.value)}
                        placeholder="Enter custom task..."
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/40 flex-1"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && newCustomTask.trim()) {
                            setContractData(prev => ({
                              ...prev,
                              customTasks: [...prev.customTasks, newCustomTask.trim()]
                            }));
                            setNewCustomTask('');
                          }
                        }}
                      />
                      <Button 
                        type="button"
                        onClick={() => {
                          if (newCustomTask.trim()) {
                            setContractData(prev => ({
                              ...prev,
                              customTasks: [...prev.customTasks, newCustomTask.trim()]
                            }));
                            setNewCustomTask('');
                          }
                        }}
                        className="bg-[#23F7DD] text-black hover:bg-[#1de0c9]"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {contractData.customTasks.length > 0 && (
                      <ul className="space-y-1">
                        {contractData.customTasks.map((task, idx) => (
                          <li key={idx} className="flex items-center justify-between bg-white/5 px-3 py-2 rounded text-white/80 text-sm">
                            <span>• {task}</span>
                            <button 
                              onClick={() => setContractData(prev => ({
                                ...prev,
                                customTasks: prev.customTasks.filter((_, i) => i !== idx)
                              }))}
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-3 pt-4 border-t border-white/10">
                  <Button onClick={handlePrint} className="bg-[#23F7DD] text-black hover:bg-[#1de0c9]">
                    <Printer className="w-4 h-4 mr-2" />
                    Print / Save as PDF
                  </Button>
                  <Button
                    onClick={handleDownloadWord}
                    className="bg-[#2B579A] text-white hover:bg-[#1e3f73]"
                    data-testid="download-word-btn"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Download Word (.doc)
                  </Button>
                  <Button onClick={handleSaveContract} variant="outline" className="border-white/20">
                    <Save className="w-4 h-4 mr-2" />
                    Save to History
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Contract Preview */}
            <Card className="bg-white/5 border-white/10 print:bg-white print:text-black">
              <CardHeader>
                <CardTitle className="text-white print:text-black">Contract Preview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-white text-black rounded-lg p-8 space-y-6" data-testid="contract-preview">
                  {/* Header */}
                  <div className="flex justify-between items-start border-b pb-4">
                    <div>
                      <h2 className="text-2xl font-bold">{CONTRACT_TRANSLATIONS.title[contractData.language]}</h2>
                      <p className="text-gray-600">{settings.companyName} • Org.nr: {settings.companyOrg}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">{t('contractDate')}:</p>
                      <p className="font-bold">{contractData.contractDate}</p>
                    </div>
                  </div>

                  {/* Section 01 - Parties */}
                  <div className="border rounded-lg overflow-hidden print-section-01" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                    <div className="bg-gray-100 px-4 py-2 flex items-center gap-3">
                      <span className="bg-[#23F7DD] text-black w-8 h-8 rounded flex items-center justify-center font-bold">01</span>
                      <span className="font-semibold">{t('parties')}</span>
                    </div>
                    <div className="p-4 grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 p-3 rounded">
                        <p className="text-sm text-gray-500 mb-1">{t('employer')}</p>
                        <p className="font-semibold">{settings.companyName} (Org.nr: {settings.companyOrg})</p>
                        <p className="text-sm text-gray-600">{settings.companyAddress}</p>
                        <p className="text-sm text-gray-600 mt-2"><strong>Warehouse:</strong> {settings.warehouse}</p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded">
                        <p className="text-sm text-gray-500 mb-1">{t('employee')}</p>
                        <div className="grid grid-cols-2 gap-1 text-sm">
                          <span className="text-gray-500">Name</span><span>{contractData.empName || '{{Full Name}}'}</span>
                          <span className="text-gray-500">Personal ID</span><span>{contractData.empId || '{{ID}}'}</span>
                          <span className="text-gray-500">Address</span><span>{contractData.empAddress || '{{Address}}'}</span>
                          <span className="text-gray-500">Phone</span><span>{contractData.empPhone || '{{Phone}}'}</span>
                          <span className="text-gray-500">Email</span><span>{contractData.empEmail || '{{Email}}'}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section 02 - Employment details */}
                  <div className="border rounded-lg overflow-hidden print-section-02" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                    <div className="bg-gray-100 px-4 py-2 flex items-center gap-3">
                      <span className="bg-[#23F7DD] text-black w-8 h-8 rounded flex items-center justify-center font-bold">02</span>
                      <span className="font-semibold">{t('employmentDetails')}</span>
                    </div>
                    <div className="p-4">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <span className="text-gray-500">{t('startDate')}</span><span>{contractData.startDate || '{{Start Date}}'}</span>
                        <span className="text-gray-500">{t('contractDate')}</span><span>{contractData.contractDate}</span>
                        <span className="text-gray-500">{t('contractType')}</span><span>{contractData.contractType}</span>
                        {contractData.contractType !== 'Permanent' && contractData.endDate && (
                          <>
                            <span className="text-gray-500 font-semibold">
                              {contractData.language === 'en' && 'Valid until'}
                              {contractData.language === 'no' && 'Gyldig til'}
                              {contractData.language === 'ro' && 'Valabil până la'}
                            </span>
                            <span className="font-semibold text-[#0a5a5a]">
                              {new Date(contractData.endDate).toLocaleDateString(
                                contractData.language === 'no' ? 'no-NO' :
                                contractData.language === 'ro' ? 'ro-RO' : 'en-GB',
                                { day: '2-digit', month: 'long', year: 'numeric' }
                              )}
                            </span>
                            <div className="col-span-2 mt-2 p-2 bg-amber-50 border-l-4 border-amber-400 rounded text-xs italic text-amber-900">
                              ⚠️{' '}
                              {contractData.language === 'en' && 'If the employee is not notified at least 14 days before the expiry date about a new contract being prepared, this contract is considered NOT renewed and employment ends automatically on the date above.'}
                              {contractData.language === 'no' && 'Dersom den ansatte ikke er varslet minst 14 dager før utløpsdatoen om at en ny kontrakt vil bli utstedt, anses denne kontrakten som IKKE fornyet, og arbeidsforholdet opphører automatisk på datoen over.'}
                              {contractData.language === 'ro' && 'Dacă angajatul nu este informat cu cel puțin 14 zile înainte de data de expirare despre încheierea unui contract nou, acest contract este considerat NEREÎNNOIT, iar raportul de muncă încetează automat la data de mai sus.'}
                            </div>
                          </>
                        )}
                        <span className="text-gray-500">{t('placeOfWork')}</span><span>{contractData.placeOfWork}</span>
                      </div>
                    </div>
                  </div>

                  {/* Section 03 - Position & responsibilities */}
                  <div
                    className={`border rounded-lg overflow-hidden print-section-03 ${showCumulative ? 'section-03-allow-break' : ''}`}
                    style={showCumulative ? {} : { pageBreakInside: 'avoid', breakInside: 'avoid' }}
                  >
                    <div className="bg-gray-100 px-4 py-2 flex items-center gap-3">
                      <span className="bg-[#23F7DD] text-black w-8 h-8 rounded flex items-center justify-center font-bold">03</span>
                      <span className="font-semibold">
                        {contractData.language === 'en' && 'Position & responsibilities'}
                        {contractData.language === 'no' && 'Stilling og ansvar'}
                        {contractData.language === 'ro' && 'Poziție și responsabilități'}
                      </span>
                    </div>
                    <div className="p-4">
                      <div className="position-card" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                        <p><strong>{contractData.language === 'en' ? 'Position' : contractData.language === 'no' ? 'Stilling' : 'Poziție'}:</strong> {currentPosition.label}</p>
                        <p className="mt-3 text-sm text-gray-500">
                          {contractData.language === 'en' && 'Role expectations'}
                          {contractData.language === 'no' && 'Rolleforventninger'}
                          {contractData.language === 'ro' && 'Așteptări de rol'}
                        </p>
                        <ul className="text-sm space-y-1">
                          {(currentPosition.responsibilities[contractData.language] || currentPosition.responsibilities.en).map((r, i) => {
                            const isHeader = /^\*\*.+\*\*$/.test(r);
                            if (isHeader) {
                              const clean = r.replace(/^\*\*|\*\*$/g, '');
                              return (
                                <li key={i} className="list-none font-bold text-gray-800 mt-2 mb-1">
                                  {clean}
                                </li>
                              );
                            }
                            return <li key={i} className="list-disc list-inside ml-2">{r}</li>;
                          })}
                          {/* Custom tasks */}
                          {contractData.customTasks.map((task, i) => (
                            <li key={`custom-${i}`} className="list-disc list-inside ml-2 text-blue-700">{task}</li>
                          ))}
                        </ul>
                      </div>

                      {/* Cumulative: show BOTH future path AND inherited base */}
                      {showCumulative && (() => {
                        const currentOrder = currentPosition.order || 0;
                        const isProgressionJob = (p) => !p.nonScaffoldProgression && (p.order || 0) > 0;

                        const higherPositions = livePositions
                          .filter(p => isProgressionJob(p) && (p.order || 0) > currentOrder && p.id !== currentPosition.id)
                          .sort((a, b) => (a.order || 0) - (b.order || 0));
                        const lowerPositions = livePositions
                          .filter(p => isProgressionJob(p) && (p.order || 0) < currentOrder && p.id !== currentPosition.id && p.responsibilities)
                          .sort((a, b) => (a.order || 0) - (b.order || 0));

                        if (higherPositions.length === 0 && lowerPositions.length === 0) return null;

                        const renderList = (list) => list.map((r, i) => {
                          const isHeader = /^\*\*.+\*\*$/.test(r);
                          if (isHeader) {
                            return <li key={i} className="list-none font-bold text-gray-900 mt-2 mb-1">{r.replace(/^\*\*|\*\*$/g, '')}</li>;
                          }
                          return <li key={i} className="list-disc list-inside ml-2">{r}</li>;
                        });

                        return (
                          <>
                            {/* Future path (blue) — only if higher positions exist */}
                            {higherPositions.length > 0 && (
                              <div
                                className="mt-4 p-3 bg-blue-50 border-l-4 border-blue-400 rounded"
                                style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}
                              >
                                <p className="text-sm font-semibold text-blue-900 mb-2">
                                  {contractData.language === 'en' && 'Career growth path — future responsibilities'}
                                  {contractData.language === 'no' && 'Karrierevei — fremtidige ansvar'}
                                  {contractData.language === 'ro' && 'Cale de creștere — responsabilități viitoare'}
                                </p>
                                {higherPositions.map(hp => (
                                  <div
                                    key={hp.id}
                                    className="mb-3 position-card"
                                    style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}
                                  >
                                    <p className="text-sm font-semibold text-gray-800">{hp.label}</p>
                                    <ul className="text-sm space-y-1 text-gray-700">{renderList(hp.responsibilities?.[contractData.language] || hp.responsibilities?.en || [])}</ul>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Inherited base (green) — only if lower positions exist */}
                            {lowerPositions.length > 0 && (() => {
                              // Filter out lower positions that have no ticked items
                              const nonEmptyLower = lowerPositions.map(lp => {
                                const list = lp.responsibilities?.[contractData.language] || lp.responsibilities?.en || [];
                                const filtered = list.filter((_, i) => selectedLowerResp[`${lp.id}:${i}`] !== false);
                                return { ...lp, _filtered: filtered };
                              }).filter(lp => lp._filtered.length > 0);

                              if (nonEmptyLower.length === 0) return null;

                              return (
                                <div
                                  className="mt-4 p-3 bg-emerald-50 border-l-4 border-emerald-400 rounded"
                                  style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}
                                >
                                  <p className="text-sm font-semibold text-emerald-900 mb-2">
                                    {contractData.language === 'en' && 'Includes selected responsibilities from lower levels'}
                                    {contractData.language === 'no' && 'Inkluderer valgte ansvar fra lavere nivåer'}
                                    {contractData.language === 'ro' && 'Include responsabilitățile selectate de la nivelurile inferioare'}
                                  </p>
                                  {nonEmptyLower.map(hp => (
                                    <div
                                      key={hp.id}
                                      className="mb-3 position-card"
                                      style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}
                                    >
                                      <p className="text-sm font-semibold text-gray-800">{hp.label}</p>
                                      <ul className="text-sm space-y-1 text-gray-700">{renderList(hp._filtered)}</ul>
                                    </div>
                                  ))}
                                </div>
                              );
                            })()}
                          </>
                        );
                      })()}

                      <p className="mt-3 text-sm text-gray-500">
                        {contractData.language === 'en' && 'Limitations / authority'}
                        {contractData.language === 'no' && 'Begrensninger / myndighet'}
                        {contractData.language === 'ro' && 'Limitări / autoritate'}
                      </p>
                      <ul className="list-disc list-inside text-sm space-y-1">
                        {(currentPosition.limitations[contractData.language] || currentPosition.limitations.en).map((l, i) => (
                          <li key={i}>{l}</li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Section 04 - Working hours */}
                  <div className="border rounded-lg overflow-hidden print-section-04" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                    <div className="bg-gray-100 px-4 py-2 flex items-center gap-3">
                      <span className="bg-[#23F7DD] text-black w-8 h-8 rounded flex items-center justify-center font-bold">04</span>
                      <span className="font-semibold">{t('workingHours')}</span>
                    </div>
                    <div className="p-4">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <span className="text-gray-500">{t('normalWorkingTime')}</span><span>{t('hoursPerWeek')}</span>
                        <span className="text-gray-500">{t('dailyBreak')}</span><span>{t('breakMinutes')}</span>
                        <span className="text-gray-500">{t('ordinaryHours')}</span><span>{contractData.workHours}</span>
                        {!currentPosition.noOvertime && (
                          <>
                            <span className="text-gray-500">{t('overtimeThreshold')}</span><span>{t('overtimeAfter40')}</span>
                          </>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-3 italic">{t('workingHoursFlexibility')}</p>
                      {!currentPosition.noOvertime && (
                        <>
                          <div className="mt-3 p-2 bg-blue-50 rounded text-sm">
                            <p className="font-medium text-blue-800">{t('transportTitle')}</p>
                            <p className="text-blue-700">{t('transportText')} <strong>044 Transport after 16:00</strong></p>
                          </div>
                          <p className="text-xs text-gray-500 mt-3">
                            {t('overtimeNote')}
                          </p>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Section 05 - Salary */}
                  <div className="border rounded-lg overflow-hidden print-section-05" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                    <div className="bg-gray-100 px-4 py-2 flex items-center gap-3">
                      <span className="bg-[#23F7DD] text-black w-8 h-8 rounded flex items-center justify-center font-bold">05</span>
                      <span className="font-semibold">{t('salary')}</span>
                    </div>
                    <div className="p-4">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <span className="text-gray-500">{t('hourlyRate')}</span><span>{contractData.hourlyRate} NOK/hour</span>
                        <span className="text-gray-500">{t('paymentDate')}</span><span>{contractData.payDate}</span>
                        {!currentPosition.noOvertime ? (
                          <>
                            <span className="text-gray-500">{t('overtime')}</span><span>{contractData.otWeekday} ({t('weekday')}) • {contractData.otSpecial} ({t('sunHolidays')})</span>
                          </>
                        ) : (
                          <>
                            <span className="text-gray-500">{t('overtime')}</span>
                            <span className="text-orange-600 font-medium">
                              {contractData.language === 'en' && 'Not applicable - Fixed hourly rate includes all work'}
                              {contractData.language === 'no' && 'Ikke aktuelt - Fast timepris inkluderer alt arbeid'}
                              {contractData.language === 'ro' && 'Nu se aplică - Tariful orar fix include toată munca'}
                            </span>
                          </>
                        )}
                        <span className="text-gray-500">{t('otpPension')}</span><span>{settings.otp}</span>
                        {contractData.empBankAccount && (
                          <>
                            <span className="text-gray-500">{t('bankAccount')}</span>
                            <span className="font-mono">{contractData.empBankAccount}</span>
                          </>
                        )}
                      </div>
                      {currentPosition.noOvertime && (
                        <div className="mt-3 p-2 bg-orange-50 rounded text-sm">
                          <p className="text-orange-700">
                            {contractData.language === 'en' && 'Salary reflects a flexible logistics and operational role including transport, warehouse and scaffolding support tasks.'}
                            {contractData.language === 'no' && 'Lønn reflekterer en fleksibel logistikk- og operasjonell rolle inkludert transport, lager og stillasstøtteoppgaver.'}
                            {contractData.language === 'ro' && 'Salariul reflectă un rol flexibil de logistică și operațional, incluzând transport, depozit și sarcini de suport pentru schele.'}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Section 06 - Equipment */}
                  <div className="border rounded-lg overflow-hidden print-section-06" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                    <div className="bg-gray-100 px-4 py-2 flex items-center gap-3">
                      <span className="bg-[#23F7DD] text-black w-8 h-8 rounded flex items-center justify-center font-bold">06</span>
                      <span className="font-semibold">{t('equipment')}</span>
                    </div>
                    <div className="p-4 text-sm space-y-2">
                      <p>{t('equipmentIntro')}</p>
                      <div className="mt-2 p-2 bg-gray-50 rounded">
                        <ul className="list-disc list-inside text-gray-700 space-y-2">
                          <li><strong>{t('responsibility')}:</strong> {t('responsibilityText')}</li>
                          <li><strong>{t('return')}:</strong> {t('returnText')}</li>
                          <li><strong>{t('replacement')}:</strong> {t('replacementText')}</li>
                          <li><strong>{t('inspection')}:</strong> {t('inspectionText')}</li>
                          <li><strong>{t('lossDamage')}:</strong> {t('lossDamageText')}</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Section 07 - Holiday */}
                  <div className="border rounded-lg overflow-hidden print-section-07" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                    <div className="bg-gray-100 px-4 py-2 flex items-center gap-3">
                      <span className="bg-[#23F7DD] text-black w-8 h-8 rounded flex items-center justify-center font-bold">07</span>
                      <span className="font-semibold">{t('holiday')}</span>
                    </div>
                    <div className="p-4 text-sm space-y-2">
                      <p>{t('holidayIntro')} <strong>Ferieloven</strong>.</p>
                      <p><strong>{t('holidayEntitlement')}:</strong> {t('weeksAndDay')}. <strong>{t('holidayPay')}:</strong> {t('holidayPayPercent')}.</p>
                      <div className="mt-2 p-2 bg-amber-50 rounded">
                        <p className="font-medium text-amber-800">{t('holidayNotice')}</p>
                        <ul className="list-disc list-inside text-amber-700 mt-1">
                          <li>{t('holiday2Months')}</li>
                          <li>{t('holidayRefuse')}</li>
                          <li>{t('holidayApproval')}</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Section 08 - Trial period */}
                  <div className="border rounded-lg overflow-hidden print-section-08" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                    <div className="bg-gray-100 px-4 py-2 flex items-center gap-3">
                      <span className="bg-[#23F7DD] text-black w-8 h-8 rounded flex items-center justify-center font-bold">08</span>
                      <span className="font-semibold">{t('trialPeriod')}</span>
                    </div>
                    <div className="p-4 text-sm">
                      <p><strong>{t('trialPeriod')}:</strong> {t('trialPeriodText')}.</p>
                      <p><strong>{t('noticeDuringTrial')}:</strong> {t('noticeDuringTrialText')}.</p>
                    </div>
                  </div>

                  {/* Section 09 - Termination */}
                  <div className="border rounded-lg overflow-hidden print-section-09" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                    <div className="bg-gray-100 px-4 py-2 flex items-center gap-3">
                      <span className="bg-[#23F7DD] text-black w-8 h-8 rounded flex items-center justify-center font-bold">09</span>
                      <span className="font-semibold">{t('termination')}</span>
                    </div>
                    <div className="p-4 text-sm space-y-3">
                      <div className="p-2 bg-red-50 rounded">
                        <p className="font-medium text-red-800">{t('first14Days')}</p>
                        <p className="text-red-700">{t('first14DaysText')}</p>
                      </div>
                      
                      <div className="p-2 bg-amber-50 rounded">
                        <p className="font-medium text-amber-800">{t('after14Days')}</p>
                        <p className="text-amber-700">{t('after14DaysText')}</p>
                      </div>
                      
                      <div className="p-2 bg-green-50 rounded">
                        <p className="font-medium text-green-800">{t('after6Months')}</p>
                        <p className="text-green-700">{t('after6MonthsRenewal')}</p>
                        <p className="text-green-700">{t('standardNotice')}</p>
                      </div>
                    </div>
                  </div>

                  {/* Section 10 - Operational Instructions */}
                  <div className="border rounded-lg overflow-hidden print-section-10" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                    <div className="bg-gray-100 px-4 py-2 flex items-center gap-3">
                      <span className="bg-[#23F7DD] text-black w-8 h-8 rounded flex items-center justify-center font-bold">10</span>
                      <span className="font-semibold">{t('operationalInstructions')}</span>
                    </div>
                    <div className="p-4 text-sm space-y-4">
                      <div className="p-3 bg-purple-50 rounded border-l-4 border-purple-500">
                        <p className="font-semibold text-purple-800 mb-1">{t('complianceTitle')}</p>
                        <p className="text-purple-700">{t('complianceText')}</p>
                      </div>
                      
                      <div className="p-3 bg-indigo-50 rounded border-l-4 border-indigo-500">
                        <p className="font-semibold text-indigo-800 mb-1">{t('flexibilityTitle')}</p>
                        <p className="text-indigo-700">{t('flexibilityText')}</p>
                      </div>
                      
                      <div className="p-3 bg-teal-50 rounded border-l-4 border-teal-500">
                        <p className="font-semibold text-teal-800 mb-1">{t('workPaceTitle')}</p>
                        <p className="text-teal-700">{t('workPaceText')}</p>
                      </div>
                    </div>
                  </div>

                  {/* Additional Notes */}
                  {contractData.notes && (
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-gray-100 px-4 py-2 flex items-center gap-3">
                        <span className="bg-[#23F7DD] text-black w-8 h-8 rounded flex items-center justify-center font-bold">+</span>
                        <span className="font-semibold">{t('additionalNotes')}</span>
                      </div>
                      <div className="p-4 text-sm">
                        <p>{contractData.notes}</p>
                      </div>
                    </div>
                  )}

                  {/* Signature Section */}
                  <div className="border-t pt-6 mt-6 print:mt-auto print:break-inside-avoid" style={{pageBreakInside: 'avoid'}}>
                    <p className="text-center text-sm text-gray-500 mb-4">
                      <strong>{t('signatureDate')}:</strong> {contractData.contractDate} &nbsp;&nbsp; <strong>{t('signaturePlace')}:</strong> Oslo
                    </p>
                    <div className="grid grid-cols-2 gap-8">
                      {/* Employer Signature */}
                      <div className="text-center">
                        <div className="border-2 border-dashed border-gray-300 rounded-lg h-32 flex items-center justify-center relative overflow-hidden">
                          {employerSig ? (
                            <img src={employerSig} alt="Employer Signature" className="max-h-28 max-w-full object-contain" />
                          ) : (
                            <span className="text-gray-400">{t('employerSignature')}</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 mt-2">{settings.companyName}</p>
                        <div className="mt-2 flex justify-center gap-2 no-print">
                          <label className="cursor-pointer text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200">
                            <input 
                              type="file" 
                              accept="image/*" 
                              className="hidden" 
                              onChange={(e) => handleSignatureUpload(e, 'employer')}
                            />
                            📷 Upload
                          </label>
                          {employerSig && (
                            <button 
                              onClick={() => clearSignature('employer')}
                              className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded hover:bg-red-200"
                            >
                              ✕ Clear
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {/* Employee Signature */}
                      <div className="text-center">
                        <div className="border-2 border-dashed border-gray-300 rounded-lg h-32 flex items-center justify-center relative overflow-hidden">
                          {employeeSig ? (
                            <img src={employeeSig} alt="Employee Signature" className="max-h-28 max-w-full object-contain" />
                          ) : (
                            <span className="text-gray-400">{t('employeeSignature')}</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 mt-2">{contractData.empName || t('employee')}</p>
                        <div className="mt-2 flex justify-center gap-2 no-print">
                          <label className="cursor-pointer text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200">
                            <input 
                              type="file" 
                              accept="image/*" 
                              className="hidden" 
                              onChange={(e) => handleSignatureUpload(e, 'employee')}
                            />
                            📷 Upload
                          </label>
                          {employeeSig && (
                            <button 
                              onClick={() => clearSignature('employee')}
                              className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded hover:bg-red-200"
                            >
                              ✕ Clear
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Employees Tab */}
          <TabsContent value="employees" className="space-y-6">
            <Card className="bg-white/5 border-white/10 text-white placeholder:text-white/40">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white">Employees</CardTitle>
                    <CardDescription className="text-white/60">Stored in your browser (localStorage). Export/import if you change device.</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={handleExportEmployees} className="border-white/20">
                      <Download className="w-4 h-4 mr-2" />
                      Export
                    </Button>
                    <label className="cursor-pointer">
                      <Button variant="outline" size="sm" className="border-white/20" asChild>
                        <span>
                          <Upload className="w-4 h-4 mr-2" />
                          Import
                        </span>
                      </Button>
                      <input type="file" accept=".json" className="hidden" onChange={handleImportEmployees} />
                    </label>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Add Employee Form */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <Input 
                    placeholder="Full name"
                    value={newEmployee.name}
                    onChange={(e) => setNewEmployee(prev => ({ ...prev, name: e.target.value }))}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                  />
                  <Input 
                    placeholder="Personal ID"
                    value={newEmployee.personalId}
                    onChange={(e) => setNewEmployee(prev => ({ ...prev, personalId: e.target.value }))}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                  />
                  <Input 
                    placeholder="Address"
                    value={newEmployee.address}
                    onChange={(e) => setNewEmployee(prev => ({ ...prev, address: e.target.value }))}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                  />
                  <Input 
                    placeholder="Phone"
                    value={newEmployee.phone}
                    onChange={(e) => setNewEmployee(prev => ({ ...prev, phone: e.target.value }))}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                  />
                  <Input 
                    placeholder="Email"
                    value={newEmployee.email}
                    onChange={(e) => setNewEmployee(prev => ({ ...prev, email: e.target.value }))}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <Input 
                    placeholder="Position (e.g., Scaffolder L2)"
                    value={newEmployee.position}
                    onChange={(e) => setNewEmployee(prev => ({ ...prev, position: e.target.value }))}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                  />
                  <Input 
                    placeholder="Bank Account (IBAN)"
                    value={newEmployee.bankAccount}
                    onChange={(e) => setNewEmployee(prev => ({ ...prev, bankAccount: e.target.value }))}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                  />
                  <Button onClick={handleAddEmployee} className="bg-[#23F7DD] text-black hover:bg-[#1de0c9]">
                    <Plus className="w-4 h-4 mr-2" />
                    Add
                  </Button>
                </div>

                {/* Employees Table */}
                <div className="rounded-lg border border-white/10 overflow-hidden mt-6">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10">
                        <TableHead className="text-white/70">Name</TableHead>
                        <TableHead className="text-white/70">ID</TableHead>
                        <TableHead className="text-white/70">Contact</TableHead>
                        <TableHead className="text-white/70">Position</TableHead>
                        <TableHead className="text-white/70">Bank Account</TableHead>
                        <TableHead className="text-white/70">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {employees.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-white/40">
                            No employees yet.
                          </TableCell>
                        </TableRow>
                      ) : (
                        employees.map(emp => (
                          <TableRow key={emp.id} className="border-white/10">
                            <TableCell className="text-white font-medium">{emp.name}</TableCell>
                            <TableCell className="text-white/70">{emp.personalId}</TableCell>
                            <TableCell className="text-white/70">
                              <div>{emp.phone}</div>
                              <div className="text-xs text-white/50">{emp.email}</div>
                            </TableCell>
                            <TableCell className="text-white/70">{emp.position || '-'}</TableCell>
                            <TableCell className="text-white/70 text-xs">{emp.bankAccount || '-'}</TableCell>
                            <TableCell>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleDeleteEmployee(emp.id)}
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contracts History Tab */}
          <TabsContent value="history" className="space-y-6">
            <Card className="bg-white/5 border-white/10 text-white placeholder:text-white/40">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white">Contracts</CardTitle>
                    <CardDescription className="text-white/60">History is stored locally in your browser. You can export for backup.</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={handleExportContracts} className="border-white/20">
                      <Download className="w-4 h-4 mr-2" />
                      Export
                    </Button>
                    <label className="cursor-pointer">
                      <Button variant="outline" size="sm" className="border-white/20" asChild>
                        <span>
                          <Upload className="w-4 h-4 mr-2" />
                          Import
                        </span>
                      </Button>
                      <input type="file" accept=".json" className="hidden" onChange={handleImportContracts} />
                    </label>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="rounded-lg border border-white/10 overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10">
                        <TableHead className="text-white/70">Date</TableHead>
                        <TableHead className="text-white/70">Employee</TableHead>
                        <TableHead className="text-white/70">Position</TableHead>
                        <TableHead className="text-white/70">Rate</TableHead>
                        <TableHead className="text-white/70">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {contracts.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-white/40">
                            No contracts in history yet.
                          </TableCell>
                        </TableRow>
                      ) : (
                        contracts.map(contract => (
                          <TableRow key={contract.id} className="border-white/10">
                            <TableCell className="text-white/70">{contract.contractDate}</TableCell>
                            <TableCell className="text-white font-medium">{contract.empName}</TableCell>
                            <TableCell className="text-white/70">{contract.positionLabel}</TableCell>
                            <TableCell className="text-white/70">{contract.hourlyRate} NOK</TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => handleLoadContract(contract)}
                                  className="text-[#23F7DD] hover:text-[#1de0c9] hover:bg-[#23F7DD]/10"
                                >
                                  Load
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => handleDeleteContract(contract.id)}
                                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            <Card className="bg-white/5 border-white/10 text-white placeholder:text-white/40">
              <CardHeader>
                <CardTitle className="text-white">Settings</CardTitle>
                <CardDescription className="text-white/60">These settings only affect this page in your browser.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-white/90 font-medium">Company name</Label>
                    <Input 
                      value={settings.companyName}
                      onChange={(e) => setSettings(prev => ({ ...prev, companyName: e.target.value }))}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                    />
                  </div>
                  <div>
                    <Label className="text-white/90 font-medium">Org.nr</Label>
                    <Input 
                      value={settings.companyOrg}
                      onChange={(e) => setSettings(prev => ({ ...prev, companyOrg: e.target.value }))}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                    />
                  </div>
                  <div>
                    <Label className="text-white/90 font-medium">OTP (pension)</Label>
                    <Input 
                      value={settings.otp}
                      onChange={(e) => setSettings(prev => ({ ...prev, otp: e.target.value }))}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-white/90 font-medium">Head office address</Label>
                    <Input 
                      value={settings.companyAddress}
                      onChange={(e) => setSettings(prev => ({ ...prev, companyAddress: e.target.value }))}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                    />
                  </div>
                  <div>
                    <Label className="text-white/90 font-medium">Rate locking</Label>
                    <Select value={settings.rateLock} onValueChange={(v) => setSettings(prev => ({ ...prev, rateLock: v }))}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white placeholder:text-white/40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="locked">Locked (rate auto from position)</SelectItem>
                        <SelectItem value="editable">Editable (admin)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-white/40 mt-1">Locked is recommended to prevent "accidental" wage changes.</p>
                  </div>
                  <div className="md:col-span-3">
                    <Label className="text-white/90 font-medium">Warehouse address(es)</Label>
                    <Input 
                      value={settings.warehouse}
                      onChange={(e) => setSettings(prev => ({ ...prev, warehouse: e.target.value }))}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                    />
                  </div>
                </div>
                
                <div className="flex gap-3 pt-4 border-t border-white/10">
                  <Button onClick={handleSaveSettings} className="bg-[#23F7DD] text-black hover:bg-[#1de0c9]">
                    <Save className="w-4 h-4 mr-2" />
                    Save Settings
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
  );
  return doserneMode
    ? (<div className="min-h-screen bg-[#0b0f14]"><div className="max-w-6xl mx-auto px-3 sm:px-5 py-4">{_content}</div></div>)
    : (<DashboardLayout>{_content}</DashboardLayout>);
}

export default HoldingContractGenerator;
